import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronLeft, FaChevronRight, FaVideo, FaVideoSlash, FaSpinner, FaTimes, FaVolumeUp, FaVolumeMute, FaDesktop, FaUserCircle, FaEye, FaCheck } from 'react-icons/fa';
import { Stage, LocalStageStream, StageEvents, StreamType, SubscribeType } from 'amazon-ivs-web-broadcast';
import intersectStaffIcon from '../images/intersect-staff-blue-icon.png';
import StagePlayer from './StagePlayer';
import virtualBgNeon from '../images/unite_banner.jpg';
import intersectBg from '../images/intersect-test-loading-05.png';
import API from '../../API';
import logger from '../../utils/logger';
import ScreenShareTour, { hasCompletedScreenShareTutorial, markScreenShareTutorialCompleted } from './ScreenShareTour';
import './UnityGame.css';
import AvatarFacePins from './AvatarFacePins';
import {
  applyFacePinStreamForParticipant,
  buildLocalFacePinStream,
  facePinStreamsRecordEqual,
  normalizeFacePinUserId,
} from './ivsFaceVideo';
import { IVS_VIDEO_CALL_CAMERA } from './ivsVideoCapturePolicy';
import type { LocalVideoControlsApi } from './localVideoControlsApi';
import type { ScreenShareControlsApi } from './screenShareControlsApi';
import { SCREEN_SHARE_QUALITY_PRESETS, type ScreenShareQuality } from './screenShareControlsApi';
import { sendIvsScreenSharePublisherToUnity, sendIvsScreenSharePublisherToUnityWithRetries } from './ivsScreenShareUnityBridge';

/** Same-tab presenter: ivs.js strips AVPro audio for this app user id (see TemplateData/ivs.js). */
function setIvsScreenShareBrowserPlaybackSuppression(active: boolean, appUserId: string | undefined) {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as unknown as {
    __ffIvsSuppressScreenPlaybackActive?: boolean;
    __ffIvsSuppressScreenPlaybackAppUserId?: string;
  };
  if (active && appUserId && String(appUserId).trim()) {
    w.__ffIvsSuppressScreenPlaybackActive = true;
    w.__ffIvsSuppressScreenPlaybackAppUserId = String(appUserId).trim();
  } else {
    w.__ffIvsSuppressScreenPlaybackActive = false;
    w.__ffIvsSuppressScreenPlaybackAppUserId = '';
  }
}

function clearIvsScreenShareUnitySync() {
  sendIvsScreenSharePublisherToUnity('', false, 'IVSRealtimePanel-shareOff');
  setIvsScreenShareBrowserPlaybackSuppression(false, undefined);
}

/** End every track (camera LED stays on if any clone or stray getUserMedia track is left live). */
function stopMediaStreamTracks(ms: MediaStream | null | undefined): void {
  if (!ms) {
    return;
  }
  ms.getTracks().forEach((t) => {
    try {
      if (t.readyState !== 'ended') {
        t.stop();
      }
    } catch {
      /* ignore */
    }
  });
}

// Background effect types
type BackgroundEffect = 'none' | 'blur' | 'virtual-neon' | 'intersect-background';

interface Participant {
  id: string;
  userId: string;
  publishedTracks: any[];
  subscribedTracks: any[];
  isIntersectStaff?: boolean;
  intersectId?: string;
}

/**
 * One remote IVS grid tile. Memoized so N participants do not re-run every video ref callback on unrelated parent renders.
 * Face pins reuse the same MediaStream instance as this tile (see STAGE_PARTICIPANT_STREAMS_ADDED).
 */
const IvsRemoteParticipantVideoTile = memo(
  function IvsRemoteParticipantVideoTile({
    participant,
    stream,
    participantVideoRefs,
  }: {
    participant: Participant;
    stream: MediaStream;
    participantVideoRefs: React.MutableRefObject<Map<string, HTMLVideoElement>>;
  }) {
    const participantId = participant.id;
    const attachRef = useCallback(
      (el: HTMLVideoElement | null) => {
        if (!el) {
          participantVideoRefs.current.delete(participantId);
          return;
        }
        participantVideoRefs.current.set(participantId, el);
        if (el.srcObject !== stream) {
          el.muted = true;
          el.srcObject = stream;
          void el.play().catch(() => {});
        }
      },
      [participantId, stream, participantVideoRefs]
    );

    return (
      <div className="relative aspect-video bg-[#0a0a0a] rounded-md overflow-hidden border border-[#333] w-full">
        <video
          ref={attachRef}
          autoPlay
          playsInline
          muted
          disablePictureInPicture
          className="h-full w-full object-cover bg-[#0a0a0a]"
        />
        <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px] font-medium truncate flex items-center gap-1">
          {participant.isIntersectStaff && (
            <img src={intersectStaffIcon} alt="Intersect Staff" className="w-3 h-3 flex-shrink-0" />
          )}
          <span className={participant.isIntersectStaff ? 'text-yellow-400' : 'text-white'}>
            {participant.userId ||
              (participant as any).info?.userId ||
              participant.id ||
              `Participant ${participant.id?.slice(0, 8) || 'unknown'}`}
          </span>
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.stream === next.stream &&
    prev.participant.id === next.participant.id &&
    prev.participant.userId === next.participant.userId &&
    prev.participant.isIntersectStaff === next.participant.isIntersectStaff
);

interface IVSRealtimePanelProps {
  stageArn?: string;
  participantToken?: string;
  accessToken?: string;
  userId?: string;
  isIntersectStaff?: boolean;
  onCollapseChange?: (isCollapsed: boolean) => void;
  onStageViewToggle?: (enabled: boolean) => void;
  onDisablePanel?: () => void; // Callback to completely disable panel (unmount for performance)
  onScreenShareChange?: (isSharing: boolean) => void; // Callback when screen sharing state changes
  hasScreenSharePermission?: boolean; // Whether user has been granted screen share permission by a mod
  isPresentationMode?: boolean; // Whether presentation mode (low-res) is active
  onTogglePresentationMode?: () => void; // Callback to toggle presentation mode
  externalCollapseControl?: boolean;
  isCollapsedExternal?: boolean;
  roomName?: string;
  debugMode?: boolean;
  isLoading?: boolean;
  stagePlaybackUrl?: string;
  isStageViewActive?: boolean;
  isStageMuted?: boolean;
  onToggleStageMute?: () => void;
  // Video call specific props - for fetching video call token
  eventId?: string;
  roomId?: string;
  // Tour/onboarding identifier
  dataTourId?: string;
  /** Parent reads local camera state/actions (e.g. self profile popover). */
  onExposeLocalVideoControls?: (api: LocalVideoControlsApi | null) => void;
  /** Parent reads screen share state/actions (dock Share popover). */
  onExposeScreenShareControls?: (api: ScreenShareControlsApi | null) => void;
  /** Called before screen share tutorial is shown so parent can open the Share dock popover (tour targets). */
  onScreenShareTutorialRequest?: () => void;
  /** When false, hide participant video grid (pins + dock controls remain). */
  showParticipantVideoGrid?: boolean;
  /** When false and grid hidden, omit large local preview tile in sidebar. */
  showLocalPreviewInSidebar?: boolean;
  /** When set, camera preview UI can portal into this element (e.g. Video dock) instead of a fullscreen modal. */
  cameraPreviewHostRef?: React.RefObject<HTMLElement | null>;
  /** Parent signals the host node is mounted (e.g. dock popover open). */
  cameraPreviewHostReady?: boolean;
  /** Hide sidebar Preview/Stop camera row — dock (or profile modal) owns camera UX. */
  dockCameraUx?: boolean;
  /**
   * When using dock camera UX: closing the preview (X / Cancel / API), after a successful **Start Camera**, and
   * after **Turn off camera**, dismisses the Video dock popover.
   */
  onCameraPreviewDismissInDock?: () => void;
  /** Parent opens the Video dock (e.g. user clicked their own face pin while publishing). */
  onRequestVideoDock?: () => void;
}

const IVSRealtimePanel: React.FC<IVSRealtimePanelProps> = ({ 
  stageArn, 
  participantToken,
  accessToken,
  userId,
  isIntersectStaff = false,
  onCollapseChange,
  onStageViewToggle,
  onDisablePanel,
  onScreenShareChange,
  hasScreenSharePermission = false,
  isPresentationMode = false,
  onTogglePresentationMode,
  externalCollapseControl = false,
  isCollapsedExternal,
  roomName = 'Video Chat',
  debugMode = false,
  isLoading = false,
  stagePlaybackUrl = '',
  isStageViewActive = false,
  isStageMuted: externalIsStageMuted,
  onToggleStageMute: externalOnToggleStageMute,
  eventId,
  roomId,
  dataTourId,
  onExposeLocalVideoControls,
  onExposeScreenShareControls,
  onScreenShareTutorialRequest,
  showParticipantVideoGrid = false,
  showLocalPreviewInSidebar = false,
  cameraPreviewHostRef,
  cameraPreviewHostReady = false,
  dockCameraUx = false,
  onCameraPreviewDismissInDock,
  onRequestVideoDock,
}) => {
  // logger.log('[IVS Panel] 🚀 COMPONENT MOUNTED/RENDERED', { 
  //   hasStageArn: !!stageArn, 
  //   hasParticipantToken: !!participantToken,
  //   userId,
  //   roomName 
  // });
  
  const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed
  const [debugParticipants, setDebugParticipants] = useState<Participant[]>([]);
  const debugParticipantVideoRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const [isStageMiniPlayerMinimized, setIsStageMiniPlayerMinimized] = useState(true); // Start minimized to save resources
  const [internalIsStageMuted, setInternalIsStageMuted] = useState(true); // Stage audio muted by default
  
  // Use external muted state if provided, otherwise use internal
  const isStageMuted = externalIsStageMuted !== undefined ? externalIsStageMuted : internalIsStageMuted;
  const handleToggleStageMute = externalOnToggleStageMute || (() => setInternalIsStageMuted(!internalIsStageMuted));
  
  // Sync with external collapse state when controlled externally
  useEffect(() => {
    if (externalCollapseControl && isCollapsedExternal !== undefined) {
      setIsCollapsed(isCollapsedExternal);
    }
  }, [externalCollapseControl, isCollapsedExternal]);
  
  const handleCollapseToggle = () => {
    if (externalCollapseControl) {
      // When externally controlled, just notify parent
      if (onCollapseChange) {
        onCollapseChange(!isCollapsed);
      }
    } else {
      // Internal control
      const newState = !isCollapsed;
      setIsCollapsed(newState);
      if (onCollapseChange) {
        onCollapseChange(newState);
      }
    }
  };
  
  // Debug mode functions
  const addDebugParticipant = () => {
    // Check if we're at the video limit
    const currentTotal = participantsWithStreams.length + (localVideoEnabled ? 1 : 0);
    if (currentTotal >= MAX_VIDEO_PARTICIPANTS) {
      logger.log('[Debug] Cannot add participant - video limit reached');
      return;
    }
    
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];
    const names = ['Alice', 'Bob', 'Charlie', 'Diana', 'Eve', 'Frank', 'Grace', 'Henry', 'Iris', 'Jack'];
    const id = `debug-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const color = colors[debugParticipants.length % colors.length];
    const name = names[debugParticipants.length % names.length];
    const isStaff = Math.random() > 0.7; // 30% chance of being staff
    
    const newParticipant: Participant = {
      id,
      userId: name,
      publishedTracks: [],
      subscribedTracks: [],
      isIntersectStaff: isStaff
    };
    
    setDebugParticipants(prev => [...prev, newParticipant]);
    
    // Create a mock video stream using canvas
    setTimeout(() => {
      const canvas = debugParticipantVideoRefs.current.get(id);
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          canvas.width = 320;
          canvas.height = 180;
          
          // Animate the canvas to show it's "live"
          let frame = 0;
          const animate = () => {
            if (!debugParticipantVideoRefs.current.has(id)) return; // Stop if participant removed
            
            ctx.fillStyle = color;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Add some animation - moving circle
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            const x = (canvas.width / 2) + Math.sin(frame * 0.05) * 50;
            const y = (canvas.height / 2) + Math.cos(frame * 0.05) * 30;
            ctx.beginPath();
            ctx.arc(x, y, 20, 0, Math.PI * 2);
            ctx.fill();
            
            // Add participant name
            ctx.fillStyle = 'white';
            ctx.font = 'bold 24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(name, canvas.width / 2, canvas.height / 2);
            
            frame++;
            requestAnimationFrame(animate);
          };
          animate();
        }
      }
    }, 100);
  };
  
  const removeDebugParticipant = () => {
    if (debugParticipants.length === 0) return;
    const lastParticipant = debugParticipants[debugParticipants.length - 1];
    debugParticipantVideoRefs.current.delete(lastParticipant.id);
    setDebugParticipants(prev => prev.slice(0, -1));
  };
  
  const clearDebugParticipants = () => {
    debugParticipantVideoRefs.current.clear();
    setDebugParticipants([]);
  };

  // Load MediaPipe Image Segmenter from CDN
  const loadSegmenter = useCallback(async () => {
    if (segmenterRef.current) return segmenterRef.current;
    
    setIsSegmenterLoading(true);
    setSegmenterError(null);
    
    try {
      // Use dynamic import with a URL string (bypass TypeScript check)
      const cdnUrl = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/+esm';
      const module = await (Function('return import("' + cdnUrl + '")')() as Promise<any>);
      const { FilesetResolver, ImageSegmenter } = module;
      
      logger.log('[IVS Panel] MediaPipe Vision loaded from CDN');
      
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm'
      );
      
      const segmenter = await ImageSegmenter.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        outputCategoryMask: true,
        outputConfidenceMasks: false
      });
      
      segmenterRef.current = segmenter;
      logger.log('[IVS Panel] ✅ MediaPipe Segmenter initialized');
      setIsSegmenterLoading(false);
      return segmenter;
    } catch (error) {
      console.error('[IVS Panel] ❌ Failed to load segmenter:', error);
      setSegmenterError('Failed to load background blur. Try again.');
      setIsSegmenterLoading(false);
      return null;
    }
  }, []);

  // Process video frame with background effect (blur or virtual background)
  const processVideoFrame = useCallback((
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    offscreenCanvas: HTMLCanvasElement,
    segmenter: any,
    effect: BackgroundEffect,
    bgImage?: HTMLImageElement | null
  ) => {
    if (!video || !canvas || !segmenter || video.readyState < 2) return;
    
    const ctx = canvas.getContext('2d');
    const offCtx = offscreenCanvas.getContext('2d');
    if (!ctx || !offCtx) return;
    
    const width = video.videoWidth;
    const height = video.videoHeight;
    
    // Ensure canvas matches video dimensions
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      offscreenCanvas.width = width;
      offscreenCanvas.height = height;
    }
    
    try {
      // Run segmentation
      const startTimeMs = performance.now();
      const result = segmenter.segmentForVideo(video, startTimeMs);
      
      if (result && result.categoryMask) {
        const mask = result.categoryMask;
        
        // Step 1: Draw background to main canvas
        if (effect === 'blur') {
          // Blur the video for background
          ctx.filter = 'blur(12px)';
          ctx.drawImage(video, 0, 0, width, height);
          ctx.filter = 'none';
        } else if (bgImage && (effect === 'virtual-neon' || effect === 'intersect-background')) {
          // Draw virtual background image, scaled to cover
          const imgAspect = bgImage.width / bgImage.height;
          const canvasAspect = width / height;
          let drawWidth, drawHeight, drawX, drawY;
          
          if (imgAspect > canvasAspect) {
            drawHeight = height;
            drawWidth = height * imgAspect;
            drawX = (width - drawWidth) / 2;
            drawY = 0;
          } else {
            drawWidth = width;
            drawHeight = width / imgAspect;
            drawX = 0;
            drawY = (height - drawHeight) / 2;
          }
          
          ctx.drawImage(bgImage, drawX, drawY, drawWidth, drawHeight);
        } else {
          // Fallback to blur
          ctx.filter = 'blur(12px)';
          ctx.drawImage(video, 0, 0, width, height);
          ctx.filter = 'none';
        }
        
        // Step 2: Draw original video to offscreen canvas
        offCtx.drawImage(video, 0, 0, width, height);
        
        // Step 3: Create mask canvas for person silhouette
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = width;
        maskCanvas.height = height;
        const maskCtx = maskCanvas.getContext('2d');
        
        if (maskCtx) {
          // Get mask data and create an image from it
          const maskData = mask.getAsUint8Array();
          const imageData = maskCtx.createImageData(width, height);
          
          // Convert mask to alpha channel (person = opaque, background = transparent)
          // MediaPipe selfie segmenter: low values = person, high values = background
          for (let i = 0; i < maskData.length; i++) {
            const pixelIndex = i * 4;
            const isPerson = maskData[i] < 128;
            imageData.data[pixelIndex] = 255;     // R
            imageData.data[pixelIndex + 1] = 255; // G
            imageData.data[pixelIndex + 2] = 255; // B
            imageData.data[pixelIndex + 3] = isPerson ? 255 : 0; // A - only show person
          }
          
          maskCtx.putImageData(imageData, 0, 0);
          
          // Step 4: Use mask to composite person on top of background
          offCtx.globalCompositeOperation = 'destination-in';
          offCtx.drawImage(maskCanvas, 0, 0);
          offCtx.globalCompositeOperation = 'source-over';
          
          // Step 5: Draw masked person over background
          ctx.drawImage(offscreenCanvas, 0, 0);
        }
        
        // Clean up mask
        mask.close();
      } else {
        // Fallback: just draw the video without effect
        ctx.drawImage(video, 0, 0, width, height);
      }
    } catch (error) {
      // On error, just draw the video without processing
      ctx.drawImage(video, 0, 0, width, height);
    }
  }, []);

  // Load virtual background image
  const loadVirtualBackground = useCallback((effect: BackgroundEffect): Promise<HTMLImageElement | null> => {
    return new Promise((resolve) => {
      if (effect === 'virtual-neon') {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = virtualBgNeon;
      } else if (effect === 'intersect-background') {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = intersectBg;
      } else {
        resolve(null);
      }
    });
  }, []);

  // Start background processing loop
  const startBackgroundProcessing = useCallback(async (
    rawStream: MediaStream,
    effect: BackgroundEffect
  ): Promise<MediaStream | null> => {
    if (effect === 'none') {
      return rawStream;
    }
    
    const segmenter = await loadSegmenter();
    if (!segmenter) {
      console.error('[IVS Panel] Segmenter not available, using raw stream');
      return rawStream;
    }
    
    // Load virtual background if needed
    let bgImage: HTMLImageElement | null = null;
    if (effect === 'virtual-neon' || effect === 'intersect-background') {
      bgImage = await loadVirtualBackground(effect);
      virtualBgImageRef.current = bgImage;
      logger.log('[IVS Panel] 🖼️ Virtual background loaded:', effect, !!bgImage);
    }
    
    // Create hidden video element for processing
    const video = document.createElement('video');
    video.srcObject = rawStream;
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    processingVideoRef.current = video;
    
    // Create canvases for processing
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 240;
    canvasRef.current = canvas;
    
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = 320;
    offscreenCanvas.height = 240;
    offscreenCanvasRef.current = offscreenCanvas;
    
    // Wait for video to be ready
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        offscreenCanvas.width = video.videoWidth;
        offscreenCanvas.height = video.videoHeight;
        video.play();
        resolve();
      };
    });
    
    // Start processing loop at ~15fps for IVS compatibility
    const targetFps = 15;
    let lastFrameTime = 0;
    const frameInterval = 1000 / targetFps;
    
    const processFrame = (currentTime: number) => {
      if (!processingVideoRef.current) {
        return;
      }
      
      // Process every frame for smooth streaming
      if (currentTime - lastFrameTime >= frameInterval) {
        processVideoFrame(video, canvas, offscreenCanvas, segmenter, effect, bgImage);
        lastFrameTime = currentTime;
      }
      
      animationFrameRef.current = requestAnimationFrame(processFrame);
    };
    
    animationFrameRef.current = requestAnimationFrame(processFrame);
    
    // Get stream from canvas - use higher fps for smoother remote viewing
    const processedStream = canvas.captureStream(targetFps);
    processedStreamRef.current = processedStream;
    
    // Ensure the video track has content before returning
    await new Promise<void>((resolve) => {
      const checkTrack = () => {
        const track = processedStream.getVideoTracks()[0];
        if (track && track.readyState === 'live') {
          resolve();
        } else {
          setTimeout(checkTrack, 50);
        }
      };
      setTimeout(checkTrack, 100);
    });
    
    logger.log('[IVS Panel] ✅ Background processing started with effect:', effect);
    return processedStream;
  }, [loadSegmenter, processVideoFrame, loadVirtualBackground]);

  // Stop background processing (but keep raw stream if specified)
  const stopBackgroundProcessing = useCallback((keepRawStream = false) => {
    logger.log('[IVS Panel] Stopping background processing...');
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    if (processingVideoRef.current) {
      processingVideoRef.current.pause();
      processingVideoRef.current.srcObject = null;
      processingVideoRef.current = null;
    }
    
    // IMPORTANT: Stop the canvas/processed stream tracks explicitly
    // This is needed for IVS to properly signal stream removal to other participants
    if (processedStreamRef.current) {
      logger.log('[IVS Panel] Stopping processed stream tracks...');
      processedStreamRef.current.getTracks().forEach(track => {
        logger.log('[IVS Panel] Stopping processed track:', track.kind, track.readyState);
        track.stop();
      });
      processedStreamRef.current = null;
    }
    
    if (!keepRawStream && rawVideoStreamRef.current) {
      logger.log('[IVS Panel] Stopping raw stream tracks...');
      rawVideoStreamRef.current.getTracks().forEach(track => track.stop());
      rawVideoStreamRef.current = null;
    }
    
    canvasRef.current = null;
    offscreenCanvasRef.current = null;
    
    logger.log('[IVS Panel] Background processing stopped');
  }, []);

  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [localVideoEnabled, setLocalVideoEnabled] = useState(false);
  const [localVideoLoading, setLocalVideoLoading] = useState(false);
  const [localAudioEnabled, setLocalAudioEnabled] = useState(true);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [streamUpdateTrigger, setStreamUpdateTrigger] = useState(0); // Force re-render when streams update
  const [participantStreams, setParticipantStreams] = useState<Map<string, MediaStream>>(new Map()); // Track streams by participant ID
  const [facePinStreamsByUserId, setFacePinStreamsByUserId] = useState<Record<string, MediaStream>>({});
  const [activePublishers, setActivePublishers] = useState<Set<string>>(new Set()); // Track which participants are actively publishing video
  const [localUsername, setLocalUsername] = useState<string>(userId || 'You');
  const [localIsIntersectStaff, setLocalIsIntersectStaff] = useState(isIntersectStaff);
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [screenShareLoading, setScreenShareLoading] = useState(false);
  const [screenShareAudioEnabled, setScreenShareAudioEnabled] = useState(false);
  const [screenShareAudioMuted, setScreenShareAudioMuted] = useState(false);
  const [screenShareQuality, setScreenShareQuality] = useState<ScreenShareQuality>('medium');
  const [availableAudioDevices, setAvailableAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string>('');
  const [backgroundEffect, setBackgroundEffect] = useState<BackgroundEffect>('none');
  const [isSegmenterLoading, setIsSegmenterLoading] = useState(false);
  const [segmenterError, setSegmenterError] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [previewEffect, setPreviewEffect] = useState<BackgroundEffect>('none');
  const [showScreenShareTutorial, setShowScreenShareTutorial] = useState(false);

  const facePinStreamsByUserIdRef = useRef<Record<string, MediaStream>>({});
  const previewStreamRef = useRef<MediaStream | null>(null);
  useEffect(() => {
    facePinStreamsByUserIdRef.current = facePinStreamsByUserId;
  }, [facePinStreamsByUserId]);
  useEffect(() => {
    previewStreamRef.current = previewStream;
  }, [previewStream]);
  
  // Video call specific state - separate IVS stage for video calls (camera/mic)
  const [videoCallToken, setVideoCallToken] = useState<string | null>(null);
  const [videoCallArn, setVideoCallArn] = useState<string | null>(null);
  const [videoCallParticipantId, setVideoCallParticipantId] = useState<string | null>(null);
  const [isVideoCallAvailable, setIsVideoCallAvailable] = useState<boolean>(true); // Assume available until proven otherwise
  const [isFetchingVideoCallToken, setIsFetchingVideoCallToken] = useState<boolean>(false);
  const [isVideoCallConnected, setIsVideoCallConnected] = useState(false); // Video call stage connection status
  const videoCallStageRef = useRef<any>(null); // Separate stage ref for video calls (camera/mic)
  
  // Screen share stage state - uses original stage_arn and participantToken
  const [isScreenShareStageConnected, setIsScreenShareStageConnected] = useState(false);
  const screenShareStageRef = useRef<any>(null); // Stage ref for screen sharing

  const stageRef = useRef<any>(null); // Legacy ref - keeping for compatibility
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const previewAnimationRef = useRef<number | null>(null);
  const previewSegmenterRef = useRef<any>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const screenShareVideoRef = useRef<HTMLVideoElement>(null);
  const participantVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const localVideoStreamRef = useRef<LocalStageStream | null>(null);
  const localAudioStreamRef = useRef<LocalStageStream | null>(null);
  const screenShareStreamRef = useRef<LocalStageStream | null>(null);
  const screenShareMediaStreamRef = useRef<MediaStream | null>(null);
  const screenShareAudioStreamRef = useRef<LocalStageStream | null>(null);
  const screenShareMicStreamRef = useRef<MediaStream | null>(null);
  const screenShareSystemAudioStreamRef = useRef<LocalStageStream | null>(null); // System audio from getDisplayMedia
  const screenShareAudioContextRef = useRef<AudioContext | null>(null); // AudioContext for processing screen share audio
  const screenShareProcessedStreamRef = useRef<MediaStream | null>(null); // Processed audio stream
  const streamsToPublishRef = useRef<any[]>([]); // Ref for video call strategy pattern (camera/mic)
  const screenShareStreamsToPublishRef = useRef<any[]>([]); // Ref for screen share strategy pattern
  const screenShareLocalParticipantIdRef = useRef<string | null>(null); // Set once stage has localParticipant; used to avoid subscribing to self (race-safe)

  // Background effect refs
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const segmenterRef = useRef<any>(null);
  const processingVideoRef = useRef<HTMLVideoElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const processedStreamRef = useRef<MediaStream | null>(null);
  const rawVideoStreamRef = useRef<MediaStream | null>(null);
  const virtualBgImageRef = useRef<HTMLImageElement | null>(null);

  // Fetch available audio devices
  useEffect(() => {
    const fetchAudioDevices = async () => {
      try {
        // Request permission first to get device labels
        await navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
          stream.getTracks().forEach(track => track.stop());
        }).catch(() => {});
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(device => device.kind === 'audioinput');
        setAvailableAudioDevices(audioInputs);
        
        // Set default device if not already set
        if (!selectedAudioDeviceId && audioInputs.length > 0) {
          setSelectedAudioDeviceId(audioInputs[0].deviceId);
        }
      } catch (error) {
        console.error('[IVS Panel] Error fetching audio devices:', error);
      }
    };
    
    fetchAudioDevices();
    
    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', fetchAudioDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', fetchAudioDevices);
    };
  }, [selectedAudioDeviceId]);

  // Fetch video call token when eventId/roomId are available
  useEffect(() => {
    const fetchVideoCallToken = async () => {
      if (!accessToken || !eventId || !roomId) {
        logger.log('[IVS Panel] Skipping video call token fetch - missing requirements', {
          hasAccessToken: !!accessToken,
          eventId,
          roomId
        });
        return;
      }

      setIsFetchingVideoCallToken(true);
      
      try {
        logger.log('[IVS Panel] Fetching video call token for room:', roomId);
        const response = await API.getIVSVideoCallTokenForRoom(accessToken, {
          eventId,
          roomId
        });

        if (response.status === 200) {
          const data = await response.json();
          logger.log('[IVS Panel] ✅ Video call token received:', {
            hasToken: !!data.video_call_token,
            participantId: data.participant_id,
            hasArn: !!data.video_call_arn
          });
          setVideoCallToken(data.video_call_token);
          setVideoCallArn(data.video_call_arn);
          setVideoCallParticipantId(data.participant_id);
          setIsVideoCallAvailable(true);
        } else if (response.status === 404) {
          logger.log('[IVS Panel] Video calls not enabled for this room');
          setIsVideoCallAvailable(false);
          setVideoCallToken(null);
          setVideoCallArn(null);
          setVideoCallParticipantId(null);
        } else {
          console.error('[IVS Panel] Failed to fetch video call token:', response.status);
          setIsVideoCallAvailable(false);
        }
      } catch (error) {
        console.error('[IVS Panel] Error fetching video call token:', error);
        setIsVideoCallAvailable(false);
      } finally {
        setIsFetchingVideoCallToken(false);
      }
    };

    fetchVideoCallToken();
  }, [accessToken, eventId, roomId]);

  // Open preview modal
  const openPreviewModal = useCallback(async () => {
    setShowPreviewModal(true);
    setPreviewEffect(backgroundEffect);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { ...IVS_VIDEO_CALL_CAMERA },
        audio: false,
      });
      setPreviewStream(stream);
    } catch (error) {
      console.error('[IVS Panel] Error getting preview stream:', error);
      setSegmenterError('Could not access camera');
    }
  }, [backgroundEffect]);

  // Close preview modal
  const closePreviewModal = useCallback(() => {
    setShowPreviewModal(false);

    // Stop preview animation
    if (previewAnimationRef.current) {
      cancelAnimationFrame(previewAnimationRef.current);
      previewAnimationRef.current = null;
    }

    // Stop preview stream
    if (previewStream) {
      previewStream.getTracks().forEach((track) => track.stop());
      setPreviewStream(null);
    }

    if (dockCameraUx) {
      onCameraPreviewDismissInDock?.();
    }
  }, [previewStream, dockCameraUx, onCameraPreviewDismissInDock]);

  const closePreviewModalRef = useRef(closePreviewModal);
  closePreviewModalRef.current = closePreviewModal;

  const [cameraPreviewDockMountEl, setCameraPreviewDockMountEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!showPreviewModal) {
      setCameraPreviewDockMountEl(null);
      return;
    }
    if (cameraPreviewHostRef && cameraPreviewHostReady) {
      setCameraPreviewDockMountEl(cameraPreviewHostRef.current);
    } else {
      setCameraPreviewDockMountEl(null);
    }
  }, [showPreviewModal, cameraPreviewHostRef, cameraPreviewHostReady]);

  // Apply preview settings and start camera
  const applyPreviewAndStart = useCallback(async () => {
    // Save the effect choice
    const effectToUse = previewEffect;
    setBackgroundEffect(effectToUse);
    setLocalVideoLoading(true);
    
    // Stop preview animation first
    if (previewAnimationRef.current) {
      cancelAnimationFrame(previewAnimationRef.current);
      previewAnimationRef.current = null;
    }
    
    // Stop the preview stream
    if (previewStream) {
      previewStream.getTracks().forEach(track => track.stop());
    }
    
    // Close modal UI
    setShowPreviewModal(false);
    setPreviewStream(null);
    
    // Now start the actual camera
    try {
      logger.log('[IVS Panel] 🟢 Starting camera with effect:', effectToUse);
      
      const rawStream = await navigator.mediaDevices.getUserMedia({
        video: { ...IVS_VIDEO_CALL_CAMERA },
        audio: false,
      });
      
      logger.log('[IVS Panel] 🟢 Got raw stream:', rawStream.getVideoTracks().length, 'tracks');
      rawVideoStreamRef.current = rawStream;
      
      let streamToUse: MediaStream;
      if (effectToUse !== 'none') {
        logger.log('[IVS Panel] 🟢 Starting background processing with effect:', effectToUse);
        const processedStream = await startBackgroundProcessing(rawStream, effectToUse);
        streamToUse = processedStream || rawStream;
        logger.log('[IVS Panel] 🟢 Using processed stream');
      } else {
        streamToUse = rawStream;
        logger.log('[IVS Panel] 🟢 Using raw stream (no effect)');
      }
      
      // Set the stream for display - the useEffect will handle attaching to video element
      setLocalStream(streamToUse);
      setLocalVideoEnabled(true);
      
      // Publish to video call stage (video_call_arn)
      if (videoCallStageRef.current && isVideoCallConnected) {
        const stageAny = videoCallStageRef.current as any;
        const videoTrack = streamToUse.getVideoTracks()[0];
        logger.log('[IVS Panel] 🟢 Video track for publish:', videoTrack?.label, videoTrack?.readyState);
        
        if (videoTrack && typeof LocalStageStream !== 'undefined') {
          const localStageStream = new (LocalStageStream as any)(videoTrack);
          streamsToPublishRef.current = [localStageStream];
          localVideoStreamRef.current = localStageStream;
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ Published to video_call_arn');
          }
        }
      } else {
        logger.log('[IVS Panel] ⚠️ Video call stage not connected, skipping publish');
      }

      setLocalVideoLoading(false);
      if (dockCameraUx) {
        onCameraPreviewDismissInDock?.();
      }
    } catch (error) {
      console.error('[IVS Panel] ❌ Error starting camera:', error);
      setLocalVideoLoading(false);
    }
  }, [
    previewEffect,
    previewStream,
    startBackgroundProcessing,
    isVideoCallConnected,
    dockCameraUx,
    onCameraPreviewDismissInDock,
  ]);

  // Clear state when room changes
  useEffect(() => {
    // Clear participants and streams when switching rooms
    logger.log('[IVS Panel] Room changed, clearing state');
    setParticipants([]);
    setParticipantStreams(new Map());
    setFacePinStreamsByUserId({});
    setActivePublishers(new Set());
    setIsConnected(false);
    setLocalVideoEnabled(false);
    setLocalStream(null);
    // Reset video call state for new room
    setVideoCallToken(null);
    setVideoCallArn(null);
    setVideoCallParticipantId(null);
    setIsVideoCallAvailable(true);
  }, [roomId]);

  // Register local camera for avatar face pins (cloned tracks: main preview keeps the live tracks).
  useEffect(() => {
    const uid = normalizeFacePinUserId(userId);
    if (!uid) {
      return undefined;
    }

    let pinnedStream: MediaStream | null = null;
    if (localStream && localVideoEnabled) {
      pinnedStream = buildLocalFacePinStream(localStream);
    }

    setFacePinStreamsByUserId((prev) => {
      const next = { ...prev };
      if (pinnedStream && pinnedStream.getTracks().length > 0) {
        next[uid] = pinnedStream;
      } else {
        delete next[uid];
      }
      return facePinStreamsRecordEqual(prev, next) ? prev : next;
    });

    return () => {
      if (pinnedStream) {
        pinnedStream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch {
            /* ignore */
          }
        });
      }
      setFacePinStreamsByUserId((prev) => {
        if (!(uid in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[uid];
        return facePinStreamsRecordEqual(prev, next) ? prev : next;
      });
    };
  }, [localStream, localVideoEnabled, userId]);

  // Show screen share tutorial when permission is first granted (parent opens dock popover first for tour targets)
  useEffect(() => {
    if (hasScreenSharePermission && !hasCompletedScreenShareTutorial()) {
      onScreenShareTutorialRequest?.();
      const id = window.setTimeout(() => setShowScreenShareTutorial(true), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [hasScreenSharePermission, onScreenShareTutorialRequest]);

  // If the tour is open on the "Start Sharing" step, the user can click the real Share button; dismiss the tour once share is on.
  useEffect(() => {
    if (!screenShareEnabled || !showScreenShareTutorial) {
      return;
    }
    markScreenShareTutorialCompleted();
    setShowScreenShareTutorial(false);
  }, [screenShareEnabled, showScreenShareTutorial]);

  // Handle preview modal processing
  useEffect(() => {
    if (!showPreviewModal || !previewStream || !previewVideoRef.current) {
      return;
    }

    const video = previewVideoRef.current;
    video.srcObject = previewStream;
    video.play().catch(console.error);

    // If any effect (blur or virtual bg), start processing
    if (previewEffect !== 'none') {
      let animationId: number;
      let segmenter: any = null;
      let bgImage: HTMLImageElement | null = null;

      const startPreviewProcessing = async () => {
        segmenter = await loadSegmenter();
        if (!segmenter || !previewCanvasRef.current) return;
        
        // Load virtual background if needed
        if (previewEffect === 'virtual-neon' || previewEffect === 'intersect-background') {
          bgImage = await new Promise<HTMLImageElement | null>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = previewEffect === 'virtual-neon' ? virtualBgNeon : intersectBg;
          });
        }
        
        previewSegmenterRef.current = segmenter;
        const canvas = previewCanvasRef.current;
        const offCanvas = document.createElement('canvas');
        
        let lastTime = 0;
        const frameInterval = 1000 / 15; // 15 fps for preview
        
        const processFrame = (currentTime: number) => {
          if (!showPreviewModal) return;
          
          if (currentTime - lastTime >= frameInterval) {
            if (video.readyState >= 2 && canvas) {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              offCanvas.width = video.videoWidth;
              offCanvas.height = video.videoHeight;
              processVideoFrame(video, canvas, offCanvas, segmenter, previewEffect, bgImage);
            }
            lastTime = currentTime;
          }
          animationId = requestAnimationFrame(processFrame);
        };
        
        animationId = requestAnimationFrame(processFrame);
        previewAnimationRef.current = animationId;
      };

      startPreviewProcessing();

      return () => {
        if (animationId) cancelAnimationFrame(animationId);
      };
    }
  }, [showPreviewModal, previewStream, previewEffect, loadSegmenter, processVideoFrame]);

  // Ensure local video stream is attached to video element when both are ready
  useEffect(() => {
    if (localVideoEnabled && localStream) {
      logger.log('[IVS Panel] 🎥 Stream attachment effect triggered', {
        hasStream: !!localStream,
        trackCount: localStream?.getVideoTracks().length,
        trackState: localStream?.getVideoTracks()[0]?.readyState
      });
      
      // Small delay to ensure video element is mounted
      const attachStream = () => {
        if (localVideoRef.current && localStream) {
          logger.log('[IVS Panel] 🎥 Attaching stream to video element');
          localVideoRef.current.srcObject = localStream;
          localVideoRef.current.play().then(() => {
            logger.log('[IVS Panel] ✅ Video playing');
            setLocalVideoLoading(false);
          }).catch((error) => {
            console.error('[IVS Panel] ❌ Error playing local video:', error);
            setLocalVideoLoading(false);
          });
        } else {
          logger.log('[IVS Panel] ⏳ Video element not ready, retrying...');
          setTimeout(attachStream, 100);
        }
      };
      
      // Start attachment with a small delay
      setTimeout(attachStream, 50);
    }
  }, [localVideoEnabled, localStream]);

  // Initialize video call stage connection
  // Uses video_call_token for publish+subscribe capabilities (camera, mic, video calls)
  useEffect(() => {
    logger.log('[IVS Panel] ==== Initialize video call stage useEffect TRIGGERED ====', {
      hasVideoCallToken: !!videoCallToken,
      hasVideoCallArn: !!videoCallArn,
      isVideoCallAvailable,
      isFetchingVideoCallToken,
      userId
    });
    
    // Wait for video call token to be fetched
    if (isFetchingVideoCallToken) {
      logger.log('[IVS Panel] Still fetching video call token, waiting...');
      return;
    }

    // If video calls not available for this room, skip connection
    if (!isVideoCallAvailable || !videoCallToken) {
      logger.log('[IVS Panel] Skipping video call stage connection - video calls not available or no token');
      return;
    }

    logger.log('[IVS Panel] Connecting to video call stage...');
    const connectToStage = async () => {
      try {
        // Create stage instance - Stage needs a strategy object with required methods
        // Based on the IVS docs, we need to provide a complete strategy
        const strategy = {
          // CRITICAL: Return false when we have no streams to publish
          // This signals to IVS that we're no longer a publisher, allowing clean removal on other participants
          shouldPublishParticipant: (): boolean => {
            const shouldPublish = streamsToPublishRef.current.length > 0;
            logger.log('[IVS Panel] 📡 shouldPublishParticipant called, returning:', shouldPublish, 'streams:', streamsToPublishRef.current.length);
            return shouldPublish;
          },
          stageStreamsToPublish: (): any[] => {
            logger.log('[IVS Panel] 📡 stageStreamsToPublish called, returning:', streamsToPublishRef.current.length, 'streams');
            return streamsToPublishRef.current;
          },
          shouldSubscribeToParticipant: (participant: any): SubscribeType => {
            return SubscribeType.AUDIO_VIDEO; // Subscribe to both but we'll mute audio (audio comes from Unity)
          },
        } as any; // Use 'as any' to bypass strict type checking
        
        // Use videoCallToken for video call stage (publish+subscribe capabilities)
        const stage = new Stage(videoCallToken, strategy);
        stageRef.current = stage;
        videoCallStageRef.current = stage;

        // Set up event listeners - using type assertion to work around TypeScript errors
        const stageAny = stage as any;
        
        // Try multiple possible event names
        const connectionStateHandler = (state: any) => {
          const stateStr = typeof state === 'string' ? state : state?.state || state?.connectionState || String(state);
          if (stateStr === 'connected' || stateStr === 'CONNECTED') {
            setIsVideoCallConnected(true);
            setIsConnected(true); // Keep legacy state for UI compatibility
            
            // Extract local participant info when connected
            if (stageAny.localParticipant) {
              const localParticipant = stageAny.localParticipant;
              const localParticipantUserId = localParticipant.userId || 
                                           localParticipant.info?.userId || 
                                           userId || 
                                           'You';
              const localParticipantUsername = localParticipant.attributes?.username || 
                                              localParticipant.info?.attributes?.username ||
                                              localParticipantUserId;
              const intersectStaffStr = localParticipant.attributes?.intersectStaff || 
                                       localParticipant.info?.attributes?.intersectStaff || 
                                       'false';
              const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
              
              setLocalUsername(localParticipantUsername);
              setLocalIsIntersectStaff(isIntersectStaff);
            }
          } else if (stateStr === 'disconnected' || stateStr === 'DISCONNECTED') {
            setIsVideoCallConnected(false);
            setIsConnected(false);
          } else if (stateStr === 'failed' || stateStr === 'FAILED') {
            console.error('❌ Failed to connect to IVS video call stage');
            setIsVideoCallConnected(false);
            setIsConnected(false);
          }
        };

        // Try different event name variations
        stageAny.on('connection-state-changed', connectionStateHandler);
        stageAny.on('connectionStateChanged', connectionStateHandler);
        stageAny.on('state-changed', connectionStateHandler);
        
        // Also use the onConnectionStateChanged method if it exists (saw it in stageKeys)
        if (typeof stageAny.onConnectionStateChanged === 'function') {
          stageAny.onConnectionStateChanged((state: any) => {
            connectionStateHandler(state);
          });
        }
        
        // Also listen for error events
        stageAny.on('error', (error: any) => {
          console.error('Stage error event:', error);
          setIsConnected(false);
        });

        // Listen for STAGE_PARTICIPANT_STREAMS_ADDED - this is the key event for getting streams!
        stageAny.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant: any, streams: any[]) => {
          const participantId = participant.id || participant.info?.id;
          logger.log('[IVS Panel] 📹 STAGE_PARTICIPANT_STREAMS_ADDED EVENT FIRED', { 
            participantId, 
            userId: participant.userId, 
            isLocal: participant.isLocal,
            streamCount: streams.length,
            timestamp: new Date().toISOString()
          });
          
          // For local participant, we already have the stream from getUserMedia
          // Don't override it with the published stream to avoid black screen
          if (participant.isLocal) {
            logger.log('[IVS Panel] 📹 Skipping STREAMS_ADDED for local participant');
            // Just ensure the local video element has the stream
            if (localVideoRef.current && localStream) {
              if (!localVideoRef.current.srcObject) {
                localVideoRef.current.srcObject = localStream;
                localVideoRef.current.play().catch((error) => {
                  console.error('Error playing local video:', error);
                });
              }
            }
            return; // Don't process local participant streams further
          }
          
          // Track that this participant is now publishing
          setActivePublishers((prev) => {
            const newSet = new Set(prev);
            newSet.add(participantId);
            logger.log('[IVS Panel] 🟢 ADDING to activePublishers:', {
              participantId,
              beforeSize: prev.size,
              afterSize: newSet.size,
              allPublishers: Array.from(newSet)
            });
            return newSet;
          });
          
          // Ensure participant is in state (for remote participants)
          setParticipants((prev) => {
            const exists = prev.find((p) => p.id === participantId);
            if (!exists) {
              // Check if participant is Intersect staff
              const intersectStaffStr = participant.attributes?.intersectStaff || 
                                       participant.info?.attributes?.intersectStaff || 
                                       'false';
              const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
              const intersectId = participant.attributes?.intersectId || participant.info?.attributes?.intersectId || '';
              
              return [...prev, {
                id: participantId,
                userId: participant.userId || participant.info?.userId || '',
                publishedTracks: [],
                subscribedTracks: [],
                isIntersectStaff,
                intersectId: intersectId || undefined
              }];
            }
            return prev;
          });
          
          // For remote participants, create MediaStream from tracks
          // Only add VIDEO tracks - audio comes from Unity, not IVS
          const mediaStream = new MediaStream();
          streams.forEach((stream: any) => {
            if (stream.mediaStreamTrack && stream.mediaStreamTrack.kind === 'video') {
              logger.log('[IVS Panel] Adding video track:', stream.mediaStreamTrack.id);
              mediaStream.addTrack(stream.mediaStreamTrack);
            } else if (stream.mediaStreamTrack) {
              logger.log('[IVS Panel] Skipping audio track (audio from Unity):', stream.mediaStreamTrack.id);
            }
          });
          
          if (mediaStream.getTracks().length > 0) {
            logger.log('[IVS Panel] Created MediaStream with', mediaStream.getTracks().length, 'tracks for participant', participantId);
            
            // Store the stream in state so we can attach it when video element is created
            setParticipantStreams((prev) => {
              const newMap = new Map(prev);
              newMap.set(participantId, mediaStream);
              logger.log('[IVS Panel] 🟢 ADDING to participantStreams:', {
                participantId,
                beforeSize: prev.size,
                afterSize: newMap.size,
                allStreams: Array.from(newMap.keys())
              });
              return newMap;
            });
            
            // Also try to attach to existing video element if it exists
            const videoElement = participantVideoRefs.current.get(participantId);
            if (videoElement) {
              logger.log('[IVS Panel] Attaching stream to existing video element for', participantId);
              videoElement.muted = true; // Ensure muted for autoplay
              videoElement.srcObject = mediaStream;
              videoElement.play().catch((error) => {
                console.error('[IVS Panel] Error playing video:', error);
              });
            } else {
              logger.log('[IVS Panel] No video element found yet for', participantId, '- will attach when element is created');
            }

            // Same MediaStream instance as the grid tile (no second MediaStream wrapper per remote).
            setFacePinStreamsByUserId((prev) => {
              const next = applyFacePinStreamForParticipant(participant, mediaStream, prev);
              return facePinStreamsRecordEqual(prev, next) ? prev : next;
            });
          } else {
            logger.warn('[IVS Panel] No tracks found in streams for participant', participantId);
          }
        });
        
        // Listen for streams removed
        stageAny.on(StageEvents.STAGE_PARTICIPANT_STREAMS_REMOVED, (participant: any, streams: any[]) => {
          const participantId = participant.id || participant.info?.id;
          logger.log('[IVS Panel] 🚫 STAGE_PARTICIPANT_STREAMS_REMOVED EVENT FIRED', { 
            participantId, 
            userId: participant.userId,
            streamCount: streams.length,
            isLocal: participant.isLocal,
            timestamp: new Date().toISOString()
          });
          
          // CRITICAL: Remove from active publishers
          setActivePublishers((prev) => {
            const hadParticipant = prev.has(participantId);
            const newSet = new Set(prev);
            const deleteResult = newSet.delete(participantId);
            logger.log('[IVS Panel] 🔴 ACTIVELY REMOVING from activePublishers:', { 
              participantId, 
              wasInSet: hadParticipant,
              deleteSuccessful: deleteResult,
              beforeSize: prev.size,
              afterSize: newSet.size,
              remainingPublishers: Array.from(newSet)
            });
            return newSet;
          });
          
          // CRITICAL: Remove stream from state
          setParticipantStreams((prev) => {
            const hadStream = prev.has(participantId);
            const newMap = new Map(prev);
            const deleteResult = newMap.delete(participantId);
            logger.log('[IVS Panel] 🔴 ACTIVELY REMOVING from participantStreams:', {
              participantId,
              wasInMap: hadStream,
              deleteSuccessful: deleteResult,
              beforeSize: prev.size,
              afterSize: newMap.size,
              remainingStreams: Array.from(newMap.keys())
            });
            return newMap;
          });

          setFacePinStreamsByUserId((prev) => {
            const next = applyFacePinStreamForParticipant(participant, null, prev);
            return facePinStreamsRecordEqual(prev, next) ? prev : next;
          });
          
          // CRITICAL: Clean up video element
          const videoElement = participantVideoRefs.current.get(participantId);
          if (videoElement) {
            logger.log('[IVS Panel] 🔴 Cleaning up video element for:', participantId);
            // Pause video to prevent play errors
            videoElement.pause();
            
            // Clear srcObject immediately when streams are removed
                videoElement.srcObject = null;
            
            // Remove the video element reference completely
            participantVideoRefs.current.delete(participantId);
            logger.log('[IVS Panel] 🔴 Video element removed from refs');
          } else {
            logger.log('[IVS Panel] ⚠️ No video element found in refs for:', participantId);
          }
        });
        
        stageAny.on(StageEvents.STAGE_PARTICIPANT_JOINED, (participant: any) => {
          setParticipants((prev) => {
            const exists = prev.find((p) => p.id === participant.id);
            if (!exists) {
              // Check if participant is Intersect staff
              const intersectStaffStr = participant.attributes?.intersectStaff || 
                                       participant.info?.attributes?.intersectStaff || 
                                       'false';
              const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
              const intersectId = participant.attributes?.intersectId || participant.info?.attributes?.intersectId || '';
              
              return [...prev, {
                id: participant.id,
                userId: participant.userId || participant.info?.userId || '',
                publishedTracks: [],
                subscribedTracks: [],
                isIntersectStaff,
                intersectId: intersectId || undefined
              }];
            }
            return prev;
          });

          // Check if participant already has published streams - try multiple property names
          const streamsToSubscribe: any[] = [];
          
          if (participant.publishedStreams && participant.publishedStreams.length > 0) {
            streamsToSubscribe.push(...participant.publishedStreams);
          }
          if (participant.streams && participant.streams.length > 0) {
            streamsToSubscribe.push(...participant.streams);
          }
          if (participant.videoStreams && participant.videoStreams.length > 0) {
            streamsToSubscribe.push(...participant.videoStreams);
          }
          if (participant.publishedTracks && participant.publishedTracks.length > 0) {
            streamsToSubscribe.push(...participant.publishedTracks);
          }
          
          streamsToSubscribe.forEach((stream: any) => {
            // Subscribe to the stream
            if (stageAny.localParticipant && typeof stageAny.localParticipant.subscribe === 'function') {
              try {
                stageAny.localParticipant.subscribe(stream);
              } catch (e) {
                console.error('Error subscribing via localParticipant:', e);
              }
            } else if (typeof stageAny.subscribe === 'function') {
              try {
                stageAny.subscribe(stream);
              } catch (e) {
                console.error('Error subscribing via stage:', e);
              }
            }
          });

          // Listen for new streams from this participant
          if (typeof participant.on === 'function') {
            participant.on('stream-published', (stream: any) => {
              // Subscribe to the stream
              if (stageAny.localParticipant && typeof stageAny.localParticipant.subscribe === 'function') {
                stageAny.localParticipant.subscribe(stream);
              } else if (typeof stageAny.subscribe === 'function') {
                stageAny.subscribe(stream);
              }
            });
          }
        });

        stageAny.on('participant-left', (participant: any) => {
          const participantId = participant.id || participant.info?.id;
          logger.log('[IVS Panel] 👋 Participant left:', participantId);
          
          // Remove from participants state
          setParticipants((prev) => prev.filter((p) => p.id !== participantId));
          
          // Remove from active publishers
          setActivePublishers((prev) => {
            const newSet = new Set(prev);
            newSet.delete(participantId);
            logger.log('[IVS Panel] Active publishers after removal:', newSet.size);
            return newSet;
          });
          
          // Remove from participant streams
          setParticipantStreams((prev) => {
            const newMap = new Map(prev);
            newMap.delete(participantId);
            logger.log('[IVS Panel] Participant streams after removal:', newMap.size);
            return newMap;
          });

          setFacePinStreamsByUserId((prev) => {
            const next = applyFacePinStreamForParticipant(participant, null, prev);
            return facePinStreamsRecordEqual(prev, next) ? prev : next;
          });
          
          // Clean up video element
          const videoElement = participantVideoRefs.current.get(participantId);
          if (videoElement) {
            logger.log('[IVS Panel] Cleaning up video element for:', participantId);
            // Pause first to prevent play errors
            videoElement.pause();
            videoElement.srcObject = null;
            participantVideoRefs.current.delete(participantId);
          }
        });

        // Try multiple event names for stream subscription
        const handleStreamSubscribed = (data: any) => {
          // Handle different data formats
          let participantId: string | undefined;
          let stream: any;
          
          if (typeof data === 'object' && data !== null) {
            participantId = data.participantId || data.participant?.id || data.participantId || data.participantId;
            stream = data.stream || data.remoteStream || data;
          } else {
            stream = data;
          }
          
          // If we have a stream but no participantId, try to get it from the stream
          if (stream && !participantId) {
            participantId = stream.participantId || stream.participant?.id;
          }
          
          if (!participantId) {
            // Try to find participant by matching stream
            let allParticipants: any[] = [];
            if (stageAny.participants instanceof Map) {
              allParticipants = Array.from(stageAny.participants.values());
            } else if (Array.isArray(stageAny.participants)) {
              allParticipants = stageAny.participants;
            }
            
            for (const p of allParticipants) {
              if (p.publishedStreams?.includes(stream) || p.streams?.includes(stream)) {
                participantId = p.id;
                break;
              }
            }
          }

          if (!participantId) {
            return;
          }

          const videoElement = participantVideoRefs.current.get(participantId);
          if (videoElement && stream) {
            try {
              // Try to get MediaStream from the stream object
              let mediaStream: MediaStream | null = null;
              if (typeof stream.getMediaStream === 'function') {
                mediaStream = stream.getMediaStream();
              } else if (stream.mediaStream) {
                mediaStream = stream.mediaStream;
              } else if (stream instanceof MediaStream) {
                mediaStream = stream;
              } else if (stream.stream && stream.stream instanceof MediaStream) {
                mediaStream = stream.stream;
              }
              
              if (mediaStream) {
                // Filter to only video tracks - audio comes from Unity, not IVS
                const videoOnlyStream = new MediaStream();
                mediaStream.getVideoTracks().forEach(track => videoOnlyStream.addTrack(track));
                
                videoElement.muted = true; // Ensure muted for autoplay
                videoElement.srcObject = videoOnlyStream;
                videoElement.play().catch((error) => {
                  console.error('Error playing video:', error);
                });
                // Force re-render to update UI
                setStreamUpdateTrigger(prev => prev + 1);
              }
            } catch (error) {
              console.error('Error setting up video element:', error);
            }
          }
        };
        
        stageAny.on('stream-subscribed', handleStreamSubscribed);
        stageAny.on('streamSubscribed', handleStreamSubscribed);
        stageAny.on('remote-stream-subscribed', handleStreamSubscribed);

        stageAny.on('stream-unsubscribed', (data: any) => {
          const participantId = data.participantId || data.id;
          logger.log('[IVS Panel] 📴 Stream unsubscribed for:', participantId);
          
          // Remove from active publishers
          setActivePublishers((prev) => {
            const newSet = new Set(prev);
            newSet.delete(participantId);
            return newSet;
          });
          
          // Remove from participant streams
          setParticipantStreams((prev) => {
            const newMap = new Map(prev);
            newMap.delete(participantId);
            return newMap;
          });
          
          // Clean up video element completely
          const videoElement = participantVideoRefs.current.get(participantId);
          if (videoElement) {
            videoElement.pause();
            videoElement.srcObject = null;
            participantVideoRefs.current.delete(participantId);
          }
        });

        // Join the video call stage - per IVS docs, use stage.join()
        try {
          if (typeof stageAny.join === 'function') {
            logger.log('[IVS Panel] 🟢 Calling stage.join() for video call stage...');
            await stageAny.join();
            logger.log('[IVS Panel] ✅ Joined video call stage successfully');
          } else {
            console.error('[IVS Panel] ❌ stage.join() not available!');
          }
        } catch (error) {
          console.error('[IVS Panel] ❌ Error joining video call stage:', error);
          throw error;
        }

        // Check initial connection state - it might be a property, not a method
        let initialState: any;
        if (typeof stageAny.getConnectionState === 'function') {
          initialState = stageAny.getConnectionState();
        } else if (stageAny.connectionState !== undefined) {
          initialState = stageAny.connectionState;
        }
        logger.log('[IVS Panel] 🔍 Initial video call stage connection state:', initialState);
        if (initialState === 'connected' || initialState === 'CONNECTED') {
          logger.log('[IVS Panel] ✅ Video call stage already connected!');
          setIsVideoCallConnected(true);
          setIsConnected(true);
        }

        // Extract local participant info (username and intersectStaff)
        if (stageAny.localParticipant) {
          const localParticipant = stageAny.localParticipant;
          const localParticipantUserId = localParticipant.userId || 
                                       localParticipant.info?.userId || 
                                       userId || 
                                       'You';
          const localParticipantUsername = localParticipant.attributes?.username || 
                                          localParticipant.info?.attributes?.username ||
                                          localParticipantUserId;
          const intersectStaffStr = localParticipant.attributes?.intersectStaff || 
                                   localParticipant.info?.attributes?.intersectStaff || 
                                   'false';
          const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
          
          setLocalUsername(localParticipantUsername);
          setLocalIsIntersectStaff(isIntersectStaff);
        }

        // Function to check and subscribe to participants
        const checkAndSubscribeToParticipants = () => {
          // logger.log('[IVS Panel] 🔍 Checking for existing participants...');
          
          // Try different ways to access participants
          let existingParticipants: any[] = [];
          
          // Try multiple ways to get participants
          if (stageAny.participants) {
            // Check if it's a Map
            if (stageAny.participants instanceof Map) {
              existingParticipants = Array.from(stageAny.participants.values());
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from Map');
            } else if (Array.isArray(stageAny.participants)) {
              existingParticipants = stageAny.participants;
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from Array');
            }
          }
          
          if (existingParticipants.length === 0) {
            // Try other methods as fallback
            if (stageAny.getParticipants && typeof stageAny.getParticipants === 'function') {
              existingParticipants = stageAny.getParticipants();
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from getParticipants()');
            } else if (stageAny.remoteParticipants && Array.isArray(stageAny.remoteParticipants)) {
              existingParticipants = stageAny.remoteParticipants;
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from remoteParticipants');
            } else if (stageAny.getAllParticipants && typeof stageAny.getAllParticipants === 'function') {
              existingParticipants = stageAny.getAllParticipants();
              // logger.log('[IVS Panel] Found', existingParticipants.length, 'participants from getAllParticipants()');
            }
          }
          
          const localParticipantId = stageAny.localParticipant?.id || stageAny.localParticipant?.info?.id;
          // logger.log('[IVS Panel] Local participant ID:', localParticipantId);
          
          existingParticipants.forEach((participant: any) => {
            // Try different ways to get participant ID
            const participantId = participant.id || participant.participantId || participant.info?.id || participant.userId;
            
            // Skip local participant
            if (participantId === localParticipantId) {
              return;
            }
            
            // Try to find streams - RemoteStageParticipant might have different properties
            const streamsToSubscribe: any[] = [];
            
            // logger.log('[IVS Panel] Processing participant:', {
            //   id: participantId,
            //   userId: participant.userId,
            //   hasPublishedStreams: !!participant.publishedStreams,
            //   hasStreams: !!participant.streams
            // });
            
            // Check various possible stream properties
            if (participant.publishedStreams && Array.isArray(participant.publishedStreams) && participant.publishedStreams.length > 0) {
              // logger.log('[IVS Panel] Found', participant.publishedStreams.length, 'published streams for', participantId);
              streamsToSubscribe.push(...participant.publishedStreams);
            }
            if (participant.streams && Array.isArray(participant.streams) && participant.streams.length > 0) {
              // logger.log('[IVS Panel] Found', participant.streams.length, 'streams for', participantId);
              streamsToSubscribe.push(...participant.streams);
            }
            if (participant.videoStreams && Array.isArray(participant.videoStreams) && participant.videoStreams.length > 0) {
              // logger.log('[IVS Panel] Found', participant.videoStreams.length, 'video streams for', participantId);
              streamsToSubscribe.push(...participant.videoStreams);
            }
            if (participant.subscribedStreams && Array.isArray(participant.subscribedStreams) && participant.subscribedStreams.length > 0) {
              // logger.log('[IVS Panel] Found', participant.subscribedStreams.length, 'subscribed streams for', participantId);
              streamsToSubscribe.push(...participant.subscribedStreams);
            }
            
            // Check if participant has a method to get streams
            if (typeof participant.getPublishedStreams === 'function') {
              try {
                const published = participant.getPublishedStreams();
                if (Array.isArray(published)) {
                  // logger.log('[IVS Panel] Got', published.length, 'streams via getPublishedStreams() for', participantId);
                  streamsToSubscribe.push(...published);
                }
              } catch (e) {
                console.error('[IVS Panel] Error calling getPublishedStreams:', e);
              }
            }
            
            // logger.log('[IVS Panel] Total streams to process for', participantId, ':', streamsToSubscribe.length);
            
            setParticipants((prev) => {
              const participantId = participant.id || (participant as any).participantId || (participant as any).info?.id || (participant as any).userId;
              const exists = prev.find((p) => {
                const pId = p.id || (p as any).participantId || (p as any).info?.id || (p as any).userId;
                return pId === participantId;
              });
              if (!exists) {
                // Check if participant is Intersect staff
                const intersectStaffStr = participant.attributes?.intersectStaff || 
                                         participant.info?.attributes?.intersectStaff || 
                                         'false';
                const isIntersectStaff = intersectStaffStr === 'true' || intersectStaffStr === true;
                const intersectId = participant.attributes?.intersectId || participant.info?.attributes?.intersectId || '';
                
                // Process existing streams - create MediaStream from tracks (same as STAGE_PARTICIPANT_STREAMS_ADDED)
                // Only add VIDEO tracks - audio comes from Unity, not IVS
                if (streamsToSubscribe.length > 0) {
                  // logger.log('[IVS Panel] Creating MediaStream from', streamsToSubscribe.length, 'streams for existing participant', participantId);
                  
                  const mediaStream = new MediaStream();
                  let trackCount = 0;
                  
                  streamsToSubscribe.forEach((stream: any) => {
                    if (stream.mediaStreamTrack && stream.mediaStreamTrack.kind === 'video') {
                      // logger.log('[IVS Panel] Adding video track for', participantId);
                      mediaStream.addTrack(stream.mediaStreamTrack);
                      trackCount++;
                    }
                  });
                  
                  if (trackCount > 0) {
                    // logger.log('[IVS Panel] ✅ Created MediaStream with', trackCount, 'tracks for existing participant', participantId);
                    
                    // Track that this participant is publishing
                    setActivePublishers((prev) => {
                      const newSet = new Set(prev);
                      newSet.add(participantId);
                      return newSet;
                    });
                    
                    // Store the stream in state
                    setParticipantStreams((prev) => {
                      const newMap = new Map(prev);
                      newMap.set(participantId, mediaStream);
                      return newMap;
                    });

                    setFacePinStreamsByUserId((prev) => {
                      const next = applyFacePinStreamForParticipant(participant, mediaStream, prev);
                      return facePinStreamsRecordEqual(prev, next) ? prev : next;
                    });
                  } else {
                    // logger.warn('[IVS Panel] ⚠️ No tracks found in streams for existing participant', participantId);
                  }
                }
                
                return [...prev, {
                  id: participantId,
                  userId: participant.userId || participant.info?.userId || participantId,
                  publishedTracks: [],
                  subscribedTracks: [],
                  isIntersectStaff,
                  intersectId: intersectId || undefined
                }];
              }
              return prev;
            });
          });
        };
        
        // Check immediately
        checkAndSubscribeToParticipants();
        
        // Also check after a delay to catch participants that join after connection
        setTimeout(() => {
          checkAndSubscribeToParticipants();
        }, 2000);
        
        setTimeout(() => {
          checkAndSubscribeToParticipants();
        }, 5000);

        // Also check connectionState property periodically for video call stage
        const checkConnectionState = () => {
          const currentState = stageAny.connectionState || (stageAny.getConnectionState && stageAny.getConnectionState());
          if (currentState === 'connected' || currentState === 'CONNECTED') {
            if (!isVideoCallConnected) {
              logger.log('[IVS Panel] ✅ Video call stage connection detected via polling');
              setIsVideoCallConnected(true);
              setIsConnected(true);
            }
          }
        };
        
        // Poll connection state every second for first 15 seconds
        const stateCheckInterval = setInterval(checkConnectionState, 1000);
        setTimeout(() => clearInterval(stateCheckInterval), 15000);

          // Periodically check for new participant streams to subscribe to
        const checkForStreams = () => {
          if (!isVideoCallConnected) return;
          
          // Try multiple ways to get participants
          let allParticipants: any[] = [];
          if (stageAny.participants) {
            // Check if it's a Map
            if (stageAny.participants instanceof Map) {
              allParticipants = Array.from(stageAny.participants.values());
            } else if (Array.isArray(stageAny.participants)) {
              allParticipants = stageAny.participants;
            }
          }
          
          if (allParticipants.length === 0) {
            // Try other methods as fallback
            if (stageAny.getParticipants && typeof stageAny.getParticipants === 'function') {
              allParticipants = stageAny.getParticipants();
            } else if (stageAny.remoteParticipants && Array.isArray(stageAny.remoteParticipants)) {
              allParticipants = stageAny.remoteParticipants;
            }
          }
          
          // Also re-run the full participant check
          checkAndSubscribeToParticipants();
          
          allParticipants.forEach((participant: any) => {
            if (!participant || participant.id === stageAny.localParticipant?.id) {
              return; // Skip self
            }
            
            // Check for streams we haven't subscribed to
            const streams = participant.publishedStreams || participant.streams || participant.videoStreams || [];
            streams.forEach((stream: any) => {
              // Check if we're already subscribed (this is a simple check)
              const videoElement = participantVideoRefs.current.get(participant.id);
              if (videoElement && !videoElement.srcObject) {
                // Try to subscribe
                if (stageAny.localParticipant && typeof stageAny.localParticipant.subscribe === 'function') {
                  try {
                    stageAny.localParticipant.subscribe(stream);
                  } catch (e) {
                    console.error('Error auto-subscribing:', e);
                  }
                }
              }
            });
          });
        };
        
        // Check for streams every 2 seconds
        const streamCheckInterval = setInterval(checkForStreams, 2000);
        setTimeout(() => clearInterval(streamCheckInterval), 60000); // Stop after 60 seconds

        // Set a timeout to detect if connection is stuck
        const connectionTimeout = setTimeout(() => {
          if (!isConnected) {
          }
        }, 10000);

        // Clear timeout if connected
        const originalHandler = connectionStateHandler;
        const wrappedHandler = (state: any) => {
          originalHandler(state);
          const stateStr = typeof state === 'string' ? state : state?.state || state?.connectionState || String(state);
          if (stateStr === 'connected' || stateStr === 'CONNECTED') {
            clearTimeout(connectionTimeout);
          }
        };

        // Re-register with wrapped handler
        stageAny.off('connection-state-changed', connectionStateHandler);
        stageAny.on('connection-state-changed', wrappedHandler);
      } catch (error) {
        console.error('❌ Error connecting to IVS video call stage:', error);
        console.error('Error details:', {
          message: (error as Error).message,
          stack: (error as Error).stack,
          name: (error as Error).name
        });
        setIsVideoCallConnected(false);
        setIsConnected(false);
      }
    };

    logger.log('[IVS Panel] 🟢 Starting video call stage connection with token length:', videoCallToken?.length);
    connectToStage();

    // Cleanup
    return () => {
      logger.log('[IVS Panel] Cleaning up stage connection...');
      
      // Stop background processing
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (processingVideoRef.current) {
        processingVideoRef.current.pause();
        processingVideoRef.current.srcObject = null;
        processingVideoRef.current = null;
      }
      if (rawVideoStreamRef.current) {
        rawVideoStreamRef.current.getTracks().forEach(track => track.stop());
        rawVideoStreamRef.current = null;
      }
      processedStreamRef.current = null;
      
      // Stop and unpublish local streams using strategy pattern
      if (localVideoStreamRef.current || localAudioStreamRef.current || screenShareStreamRef.current || screenShareAudioStreamRef.current) {
        try {
          const stageAny = stageRef.current as any;
          // Clear all streams from ref
          streamsToPublishRef.current = [];
          
          // Refresh strategy to unpublish all
          if (stageAny && typeof stageAny.refreshStrategy === 'function') {
            stageAny.refreshStrategy().catch((e: any) => {
              console.error('Error refreshing strategy on cleanup:', e);
            });
          }
        } catch (e) {
          console.error('Error unpublishing streams on cleanup:', e);
        }
        localVideoStreamRef.current = null;
        localAudioStreamRef.current = null;
        screenShareStreamRef.current = null;
        screenShareAudioStreamRef.current = null;
      }
      
      // Stop screen share mic tracks
      if (screenShareMicStreamRef.current) {
        screenShareMicStreamRef.current.getTracks().forEach((track) => {
          track.stop();
          logger.log('[IVS Panel] Stopped screen share mic track:', track.kind);
        });
        screenShareMicStreamRef.current = null;
      }
      
      // Stop local media tracks
      if (localStream) {
        localStream.getTracks().forEach((track) => {
          track.stop();
          logger.log('[IVS Panel] Stopped track:', track.kind);
        });
      }
      
      // Stop screen share tracks
      if (screenShareMediaStreamRef.current) {
        screenShareMediaStreamRef.current.getTracks().forEach((track) => {
          track.stop();
          logger.log('[IVS Panel] Stopped screen share track:', track.kind);
        });
        screenShareMediaStreamRef.current = null;
      }
      
      // Disconnect from video call stage
      if (videoCallStageRef.current) {
        const stageAny = videoCallStageRef.current as any;
        try {
        if (stageAny.leave) {
          stageAny.leave();
            logger.log('[IVS Panel] Left video call stage');
        } else if (stageAny.disconnect) {
          stageAny.disconnect();
            logger.log('[IVS Panel] Disconnected from video call stage');
          }
        } catch (e) {
          console.error('Error disconnecting video call stage:', e);
        }
        videoCallStageRef.current = null;
        stageRef.current = null; // Clear legacy ref too
      }
      
      // Clear video element refs
      participantVideoRefs.current.clear();
      
      // Clear participant streams and active publishers
      setParticipantStreams(new Map());
      setFacePinStreamsByUserId({});
      setActivePublishers(new Set());
      setIsVideoCallConnected(false);
      
      logger.log('[IVS Panel] Video call stage cleanup complete');
    };
  }, [videoCallToken, videoCallArn, isVideoCallAvailable, isFetchingVideoCallToken]); // Use video call token for video call stage

  // Initialize screen share stage connection
  // Uses participantToken for screen sharing to stage_arn (separate from video calls)
  useEffect(() => {
    logger.log('[IVS Panel] ==== Initialize screen share stage useEffect TRIGGERED ====', {
      hasStageArn: !!stageArn,
      hasParticipantToken: !!participantToken,
      userId
    });
    
    if (!stageArn || !participantToken) {
      logger.log('[IVS Panel] Skipping screen share stage connection - missing requirements');
      return;
    }

    logger.log('[IVS Panel] Connecting to screen share stage...');
    const connectToScreenShareStage = async () => {
      try {
        const strategy = {
          shouldPublishParticipant: (): boolean => {
            const shouldPublish = screenShareStreamsToPublishRef.current.length > 0;
            logger.log('[IVS Panel] 📡 [ScreenShare] shouldPublishParticipant called, returning:', shouldPublish);
            return shouldPublish;
          },
          stageStreamsToPublish: (): any[] => {
            logger.log('[IVS Panel] 📡 [ScreenShare] stageStreamsToPublish called, returning:', screenShareStreamsToPublishRef.current.length, 'streams');
            return screenShareStreamsToPublishRef.current;
          },
          shouldSubscribeToParticipant: (participant: any): SubscribeType => {
            // Don't subscribe to self (presenter) - avoids double audio when sharing system audio
            if (participant.isLocal === true) return SubscribeType.NONE;
            // Fallback: isLocal can be set late by the SDK (race). Identify self by userId from token.
            const pUserId = participant.userId ?? participant.info?.userId;
            if (userId && pUserId && String(pUserId) === String(userId)) return SubscribeType.NONE;
            // Fallback: once we've seen localParticipant, skip by id (handles re-subscription after reconnect)
            const localId = screenShareLocalParticipantIdRef.current;
            const pId = participant.id ?? participant.info?.id;
            if (localId && pId && pId === localId) return SubscribeType.NONE;
            return SubscribeType.AUDIO_VIDEO; // Subscribe to both (audio comes from Unity for viewers)
          },
        } as any;
        
        const stage = new Stage(participantToken, strategy);
        screenShareStageRef.current = stage;

        const stageAny = stage as any;
        screenShareLocalParticipantIdRef.current = null; // Reset on new connection

        const connectionStateHandler = (state: any) => {
          const stateStr = typeof state === 'string' ? state : state?.state || state?.connectionState || String(state);
          if (stateStr === 'connected' || stateStr === 'CONNECTED') {
            setIsScreenShareStageConnected(true);
            logger.log('[IVS Panel] ✅ Screen share stage connected');
            const localId = stageAny.localParticipant?.id ?? stageAny.localParticipant?.info?.id ?? null;
            if (localId) screenShareLocalParticipantIdRef.current = localId;
          } else if (stateStr === 'disconnected' || stateStr === 'DISCONNECTED') {
            setIsScreenShareStageConnected(false);
          } else if (stateStr === 'failed' || stateStr === 'FAILED') {
            console.error('❌ Failed to connect to screen share stage');
            setIsScreenShareStageConnected(false);
          }
        };

        stageAny.on('connection-state-changed', connectionStateHandler);
        stageAny.on('connectionStateChanged', connectionStateHandler);
        stageAny.on('state-changed', connectionStateHandler);
        
        if (typeof stageAny.onConnectionStateChanged === 'function') {
          stageAny.onConnectionStateChanged(connectionStateHandler);
        }
        
        stageAny.on('error', (error: any) => {
          console.error('[IVS Panel] Screen share stage error:', error);
          setIsScreenShareStageConnected(false);
        });

        // Join the stage
        try {
          if (typeof stageAny.join === 'function') {
            await stageAny.join();
            logger.log('[IVS Panel] ✅ Joined screen share stage');
          }
          // Store local participant id so shouldSubscribeToParticipant can skip self even if isLocal is set late
          const localId = stageAny.localParticipant?.id ?? stageAny.localParticipant?.info?.id ?? null;
          if (localId) screenShareLocalParticipantIdRef.current = localId;
        } catch (error) {
          console.error('[IVS Panel] Error joining screen share stage:', error);
          throw error;
        }

        // Check initial connection state
        let initialState: any;
        if (typeof stageAny.getConnectionState === 'function') {
          initialState = stageAny.getConnectionState();
        } else if (stageAny.connectionState !== undefined) {
          initialState = stageAny.connectionState;
        }
        if (initialState === 'connected' || initialState === 'CONNECTED') {
          setIsScreenShareStageConnected(true);
          // Ensure local participant id is set (in case it wasn't available immediately after join)
          const localId = stageAny.localParticipant?.id ?? stageAny.localParticipant?.info?.id ?? null;
          if (localId) screenShareLocalParticipantIdRef.current = localId;
        }

      } catch (error) {
        console.error('[IVS Panel] ❌ Error connecting to screen share stage:', error);
        setIsScreenShareStageConnected(false);
      }
    };

    connectToScreenShareStage();

    // Cleanup
    return () => {
      logger.log('[IVS Panel] Cleaning up screen share stage connection...');
      screenShareLocalParticipantIdRef.current = null;
      // Clear streams from publish strategy
      screenShareStreamsToPublishRef.current = [];
      
      // Disconnect from screen share stage
      if (screenShareStageRef.current) {
        const stageAny = screenShareStageRef.current as any;
        try {
          if (stageAny.leave) {
            stageAny.leave();
            logger.log('[IVS Panel] Left screen share stage');
          } else if (stageAny.disconnect) {
            stageAny.disconnect();
            logger.log('[IVS Panel] Disconnected from screen share stage');
          }
        } catch (e) {
          console.error('[IVS Panel] Error disconnecting screen share stage:', e);
        }
        screenShareStageRef.current = null;
      }
      
      setIsScreenShareStageConnected(false);
      logger.log('[IVS Panel] Screen share stage cleanup complete');
    };
  }, [stageArn, participantToken, userId]); // Use original stage for screen sharing; userId used to avoid subscribing to self

  // Effect to attach streams to video elements when streams arrive after elements are created
  // This handles the case where a user joins a room with existing participants
  useEffect(() => {
    // Clean up orphaned streams (streams without participants)
    const orphanedStreams: string[] = [];
    participantStreams.forEach((stream, participantId) => {
      const participantExists = participants.some(p => p.id === participantId);
      if (!participantExists) {
        orphanedStreams.push(participantId);
      }
    });
    
    if (orphanedStreams.length > 0) {
      logger.log('[IVS Panel] 🧹 Cleaning up orphaned streams:', orphanedStreams);
      setParticipantStreams((prev) => {
        const newMap = new Map(prev);
        orphanedStreams.forEach(id => newMap.delete(id));
        return newMap;
      });
      setActivePublishers((prev) => {
        const newSet = new Set(prev);
        orphanedStreams.forEach(id => newSet.delete(id));
        return newSet;
      });
      // Don't continue with attachment if we're cleaning up
      return;
    }
    
    // For each participant with a stream, make sure their video element has it
    participantStreams.forEach((stream, participantId) => {
      const videoElement = participantVideoRefs.current.get(participantId);
      if (videoElement && videoElement.srcObject !== stream) {
        videoElement.muted = true;
        videoElement.srcObject = stream;
        void videoElement.play().catch((error) => {
          console.error('[IVS Panel] Error playing video for', participantId, error);
        });
      }
    });
  }, [participantStreams, streamUpdateTrigger, participants]);

  // Handle local video/audio toggle - uses video call stage (video_call_arn)
  const toggleLocalVideoRef = useRef<() => Promise<void>>(async () => {});
  const toggleLocalVideo = async () => {
    const stageConnected = Boolean(videoCallStageRef.current && isVideoCallConnected);

    try {
      if (localVideoEnabled) {
        logger.log('[IVS Panel] 🔴 TURNING OFF LOCAL VIDEO...');

        // Face pins use MediaStreamTrack.clone(); stopping `localStream` does not stop those tracks (macOS camera LED).
        const pinUid = normalizeFacePinUserId(userId);
        if (pinUid) {
          stopMediaStreamTracks(facePinStreamsByUserIdRef.current[pinUid]);
          setFacePinStreamsByUserId((prev) => {
            if (!(pinUid in prev)) {
              return prev;
            }
            const next = { ...prev };
            delete next[pinUid];
            return facePinStreamsRecordEqual(prev, next) ? prev : next;
          });
        }

        if (previewAnimationRef.current) {
          cancelAnimationFrame(previewAnimationRef.current);
          previewAnimationRef.current = null;
        }
        setShowPreviewModal(false);
        const previewMs = previewStreamRef.current;
        if (previewMs) {
          stopMediaStreamTracks(previewMs);
          setPreviewStream(null);
        }

        const streamToStop = localStream;

        if (!stageConnected) {
          logger.log('[IVS Panel] Video call stage not connected; stopping local camera tracks only');
          stopMediaStreamTracks(streamToStop);
          stopBackgroundProcessing(false);
          if (localVideoRef.current) {
            localVideoRef.current.pause();
            localVideoRef.current.srcObject = null;
          }
          setLocalVideoEnabled(false);
          setLocalStream(null);
          setLocalVideoLoading(false);
          if (dockCameraUx) {
            onCameraPreviewDismissInDock?.();
          }
          return;
        }

        const stageAny = videoCallStageRef.current as any;

        try {
          // Step 1: Stop media tracks FIRST (critical for IVS cleanup)
          if (streamToStop) {
            logger.log('[IVS Panel] 🔴 Step 1: Stopping local stream tracks...');
            streamToStop.getTracks().forEach((track) => {
              logger.log('[IVS Panel] 🔴 Stopping track:', track.kind, track.id, track.readyState);
              track.stop();
            });
          }
          
          // Step 2: Stop background processing (canvas/raw streams)
          logger.log('[IVS Panel] 🔴 Step 2: Stopping background processing...');
          stopBackgroundProcessing(false);
          
          // Step 3: Wait for tracks to fully end before refreshStrategy
          // IVS needs time to detect track ended state for canvas streams
          logger.log('[IVS Panel] 🔴 Step 3: Waiting for tracks to end...');
          await new Promise(resolve => setTimeout(resolve, 150));
          
          // Step 4: Clear streams from publish strategy
          logger.log('[IVS Panel] 🔴 Step 4: Clearing streams from publish strategy...');
          streamsToPublishRef.current = [];
          localVideoStreamRef.current = null;
          
          // Step 5: Call refreshStrategy to unpublish from IVS
          if (stageAny && typeof stageAny.refreshStrategy === 'function') {
            logger.log('[IVS Panel] 🔴 Step 5: Calling refreshStrategy to unpublish...');
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ refreshStrategy completed');
          }
          
          // Step 6: Safety check - remove local participant from tracking if somehow added
          const localParticipantId = stageAny?.localParticipant?.id || stageAny?.localParticipant?.info?.id;
          if (localParticipantId) {
            logger.log('[IVS Panel] 🔴 Step 6: Safety check - Removing local participant from tracking:', localParticipantId);
            setActivePublishers((prev) => {
              if (prev.has(localParticipantId)) {
                const newSet = new Set(prev);
                newSet.delete(localParticipantId);
                logger.log('[IVS Panel] 🔴 Removed local participant from activePublishers');
                return newSet;
              }
              logger.log('[IVS Panel] 🔴 Local participant was not in activePublishers');
              return prev;
            });
            setParticipantStreams((prev) => {
              if (prev.has(localParticipantId)) {
                const newMap = new Map(prev);
                newMap.delete(localParticipantId);
                logger.log('[IVS Panel] 🔴 Removed local participant from participantStreams');
                return newMap;
              }
              logger.log('[IVS Panel] 🔴 Local participant was not in participantStreams');
              return prev;
            });
          } else {
            logger.log('[IVS Panel] ⚠️ Could not get local participant ID for cleanup');
          }
        } catch (error) {
          console.error('[IVS Panel] ❌ Error during unpublish:', error);
          stopBackgroundProcessing(false);
        }
        
        // Step 7: Clear the local video element UI
        if (localVideoRef.current) {
          logger.log('[IVS Panel] 🔴 Step 7: Clearing local video element');
          localVideoRef.current.pause();
          localVideoRef.current.srcObject = null;
        }
        
        // Step 8: Update UI state LAST
        setLocalVideoEnabled(false);
        setLocalStream(null);
        setLocalVideoLoading(false);

        logger.log('[IVS Panel] ✅ LOCAL VIDEO COMPLETELY TURNED OFF');
        logger.log('[IVS Panel] ✅ Other participants should see clean removal (no frozen frame)');

        if (dockCameraUx) {
          onCameraPreviewDismissInDock?.();
        }
      } else {
        if (!stageConnected) {
          logger.log('[IVS Panel] Video call stage not connected');
          return;
        }

        logger.log('[IVS Panel] 🟢 TURNING ON LOCAL VIDEO - Starting publish process...');
        
        // Start loading state
        setLocalVideoLoading(true);
        
        // Start video with efficient constraints (480p for better performance)
        const rawStream = await navigator.mediaDevices.getUserMedia({
          video: { ...IVS_VIDEO_CALL_CAMERA },
          audio: false,
        });
        
        logger.log('[IVS Panel] 🟢 Got local video stream with', rawStream.getVideoTracks().length, 'tracks');
        
        // Store raw stream reference
        rawVideoStreamRef.current = rawStream;
        
        // Apply background effect if enabled
        let stream: MediaStream;
        if (backgroundEffect !== 'none') {
          logger.log('[IVS Panel] 🟢 Applying background effect:', backgroundEffect);
          const processedStream = await startBackgroundProcessing(rawStream, backgroundEffect);
          stream = processedStream || rawStream;
        } else {
          stream = rawStream;
        }
        
        // Set stream and enable video immediately so element renders
        setLocalStream(stream);
        setLocalVideoEnabled(true);
        
        // Use setTimeout to ensure the video element is rendered before attaching stream
        // The useEffect will also handle this, but this ensures it happens quickly
        setTimeout(() => {
          if (localVideoRef.current && stream) {
            localVideoRef.current.srcObject = stream;
            localVideoRef.current.play().then(() => {
              // Video is playing, hide loading indicator
              setLocalVideoLoading(false);
            }).catch((error) => {
              console.error('Error playing local video:', error);
              setLocalVideoLoading(false);
            });
          }
        }, 0);

        // Create LocalStageStream from MediaStream
        // LocalStageStream constructor might need MediaStreamTrack(s) or different parameters
        const stageAny = videoCallStageRef.current as any;
        let localStageStream: any;
        
        try {
          // LocalStageStream likely needs MediaStreamTrack, not MediaStream
          const videoTrack = stream.getVideoTracks()[0];
          if (!videoTrack) {
            throw new Error('No video track found in stream');
          }
          
          if (typeof LocalStageStream !== 'undefined') {
            // Try with video track first (most likely)
            localStageStream = new (LocalStageStream as any)(videoTrack);
          } else {
            throw new Error('LocalStageStream not available');
          }
        } catch (e1) {
          try {
            // Fallback: try with MediaStream
            if (typeof LocalStageStream !== 'undefined') {
              localStageStream = new (LocalStageStream as any)(stream);
            } else {
              throw e1;
            }
          } catch (e2) {
            console.error('Failed to create LocalStageStream:', { e1, e2 });
            throw e2;
          }
        }
        
        // Publish the stream to video call stage (video_call_arn)
        try {
          logger.log('[IVS Panel] 🟢 Adding stream to streamsToPublishRef (video call stage)...');
          // Add stream to the ref
          streamsToPublishRef.current = [localStageStream];
          
          logger.log('[IVS Panel] 🟢 Calling refreshStrategy to publish to video_call_arn...');
          // Refresh strategy to publish the stream
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ Successfully published video stream to video_call_arn');
          } else {
            console.error('[IVS Panel] ❌ No refreshStrategy method found');
            throw new Error('refreshStrategy method not available');
          }
        } catch (publishError) {
          console.error('[IVS Panel] ❌ Error publishing stream:', publishError);
          throw publishError;
        }
        
        localVideoStreamRef.current = localStageStream;
        logger.log('[IVS Panel] 🟢 LOCAL VIDEO TURNED ON (video_call_arn)');
        // localVideoEnabled is already set to true above
      }
    } catch (error) {
      console.error('Error toggling video:', error);
    }
  };
  toggleLocalVideoRef.current = toggleLocalVideo;

  // Toggle local audio - uses video call stage (video_call_arn)
  const toggleLocalAudio = async () => {
    if (!videoCallStageRef.current || !isVideoCallConnected) {
      logger.log('[IVS Panel] Video call stage not connected');
      return;
    }

    try {
      if (localAudioEnabled) {
        // Stop audio - unpublish using strategy pattern
        if (localAudioStreamRef.current) {
          const stageAny = videoCallStageRef.current as any;
          try {
            // Remove stream from ref
            streamsToPublishRef.current = streamsToPublishRef.current.filter(
              s => s !== localAudioStreamRef.current
            );
            
            // Refresh strategy to unpublish
            if (typeof stageAny.refreshStrategy === 'function') {
              await stageAny.refreshStrategy();
              logger.log('[IVS Panel] Successfully unpublished audio from video_call_arn');
            }
          } catch (error) {
            console.error('Error unpublishing audio:', error);
          }
          localAudioStreamRef.current = null;
        }
        if (localStream) {
          localStream.getAudioTracks().forEach((track) => track.stop());
        }
        setLocalAudioEnabled(false);
      } else {
        // Start audio
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: false, 
          audio: true 
        });
        
        // Create LocalStageStream from MediaStream for audio
        const stageAny = videoCallStageRef.current as any;
        let localStageStream: any;
        
        try {
          // LocalStageStream likely needs MediaStreamTrack, not MediaStream
          const audioTrack = stream.getAudioTracks()[0];
          if (!audioTrack) {
            throw new Error('No audio track found in stream');
          }
          
          if (typeof LocalStageStream !== 'undefined') {
            // Try with audio track first (most likely)
            localStageStream = new (LocalStageStream as any)(audioTrack);
          } else {
            throw new Error('LocalStageStream not available');
          }
        } catch (e1) {
          try {
            // Fallback: try with MediaStream
            if (typeof LocalStageStream !== 'undefined') {
              localStageStream = new (LocalStageStream as any)(stream);
            } else {
              throw e1;
            }
          } catch (e2) {
            console.error('Failed to create LocalStageStream for audio:', { e1, e2 });
            throw e2;
          }
        }
        
        // Publish to video call stage (video_call_arn)
        try {
          // Add stream to the ref
          streamsToPublishRef.current = [...streamsToPublishRef.current, localStageStream];
          
          // Refresh strategy to publish the stream
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] Successfully published audio to video_call_arn');
          } else {
            console.error('No refreshStrategy method found');
            throw new Error('refreshStrategy method not available');
          }
        } catch (publishError) {
          console.error('Error publishing audio stream:', publishError);
          throw publishError;
        }
        
        localAudioStreamRef.current = localStageStream;
        setLocalAudioEnabled(true);
      }
    } catch (error) {
      console.error('Error toggling audio:', error);
    }
  };

  const toggleScreenShare = async () => {
    // Screen sharing uses the screen share stage (stage_arn)
    if (!screenShareStageRef.current || !isScreenShareStageConnected) {
      logger.log('[IVS Panel] Screen share stage not connected');
      return;
    }

    // Show tutorial on first share attempt if not completed
    if (!screenShareEnabled && !hasCompletedScreenShareTutorial()) {
      setShowScreenShareTutorial(true);
      return; // Don't proceed with share until tutorial is done
    }

    try {
      if (screenShareEnabled) {
        logger.log('[IVS Panel] 🔴 TURNING OFF SCREEN SHARE...');
        
        const stageAny = screenShareStageRef.current as any;
        const streamToUnpublish = screenShareStreamRef.current;
        const audioStreamToUnpublish = screenShareAudioStreamRef.current;
        
        try {
          // Stop the screen share tracks
          if (screenShareMediaStreamRef.current) {
            screenShareMediaStreamRef.current.getTracks().forEach((track) => {
              logger.log('[IVS Panel] 🔴 Stopping screen share track:', track.kind, track.id);
              track.stop();
            });
          }
          
          // Stop the microphone tracks if audio was enabled
          if (screenShareMicStreamRef.current) {
            screenShareMicStreamRef.current.getTracks().forEach((track) => {
              logger.log('[IVS Panel] 🔴 Stopping mic track:', track.kind, track.id);
              track.stop();
            });
            screenShareMicStreamRef.current = null;
          }
          
          // Remove from screen share streams (not video call streams)
          if (streamToUnpublish) {
            screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
              s => s !== streamToUnpublish
            );
          }
          if (audioStreamToUnpublish) {
            screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
              s => s !== audioStreamToUnpublish
            );
          }
          // Also remove system audio stream if it exists
          if (screenShareSystemAudioStreamRef.current) {
            screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
              s => s !== screenShareSystemAudioStreamRef.current
            );
            screenShareSystemAudioStreamRef.current = null;
          }
          
          // Clean up audio processing
          if (screenShareAudioContextRef.current) {
            screenShareAudioContextRef.current.close().catch(() => {});
            screenShareAudioContextRef.current = null;
          }
          if (screenShareProcessedStreamRef.current) {
            screenShareProcessedStreamRef.current.getTracks().forEach(t => t.stop());
            screenShareProcessedStreamRef.current = null;
          }
          
          // Refresh screen share stage strategy to unpublish
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ Screen share unpublished via refreshStrategy');
          }
          
          screenShareStreamRef.current = null;
          screenShareMediaStreamRef.current = null;
          screenShareAudioStreamRef.current = null;
        } catch (error) {
          console.error('[IVS Panel] ❌ Error during screen share unpublish:', error);
        }
        
        setScreenShareEnabled(false);
        setScreenShareAudioEnabled(false);
        setScreenShareAudioMuted(false);
        setScreenShareLoading(false);
        onScreenShareChange?.(false);
        clearIvsScreenShareUnitySync();
        logger.log('[IVS Panel] ✅ SCREEN SHARE TURNED OFF');
      } else {
        logger.log('[IVS Panel] 🟢 TURNING ON SCREEN SHARE (using stage_arn)...');
        
        setScreenShareLoading(true);
        
        // Get quality settings from selected preset
        const qualityConfig = SCREEN_SHARE_QUALITY_PRESETS[screenShareQuality];
        logger.log('[IVS Panel] 📺 Using screen share quality:', screenShareQuality, qualityConfig);
        
        // Request screen share with optimized audio options
        const stream = await navigator.mediaDevices.getDisplayMedia({ 
          video: {
            width: { ideal: qualityConfig.width },
            height: { ideal: qualityConfig.height },
            frameRate: { ideal: qualityConfig.frameRate }
          },
          audio: {
            // Request stereo audio with good sample rate
            channelCount: { ideal: 2 },
            sampleRate: { ideal: 48000 },
            sampleSize: { ideal: 16 },
            // Disable processing that can cause choppiness with system audio
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          }
        });
        
        logger.log('[IVS Panel] 🟢 Got screen share stream with', stream.getVideoTracks().length, 'video tracks');
        
        // Listen for when user stops sharing via browser UI
        stream.getVideoTracks()[0].onended = () => {
          logger.log('[IVS Panel] 📴 Screen share ended by user');
          setScreenShareEnabled(false);
          setScreenShareLoading(false);
          setScreenShareAudioEnabled(false);
          setScreenShareAudioMuted(false);
          onScreenShareChange?.(false);
          clearIvsScreenShareUnitySync();
          
          // Clean up from screen share publish ref
          if (screenShareStageRef.current) {
            const stageAny = screenShareStageRef.current as any;
            // Remove video stream
            if (screenShareStreamRef.current) {
              screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
                s => s !== screenShareStreamRef.current
              );
            }
            // Remove system audio stream
            if (screenShareSystemAudioStreamRef.current) {
              screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
                s => s !== screenShareSystemAudioStreamRef.current
              );
              screenShareSystemAudioStreamRef.current = null;
            }
            // Clean up audio processing
            if (screenShareAudioContextRef.current) {
              screenShareAudioContextRef.current.close().catch(() => {});
              screenShareAudioContextRef.current = null;
            }
            if (screenShareProcessedStreamRef.current) {
              screenShareProcessedStreamRef.current.getTracks().forEach(t => t.stop());
              screenShareProcessedStreamRef.current = null;
            }
            // Remove mic audio stream if enabled
            if (screenShareAudioStreamRef.current) {
              screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
                s => s !== screenShareAudioStreamRef.current
              );
              screenShareAudioStreamRef.current = null;
            }
            // Stop mic stream tracks
            if (screenShareMicStreamRef.current) {
              screenShareMicStreamRef.current.getTracks().forEach(track => track.stop());
              screenShareMicStreamRef.current = null;
            }
            if (typeof stageAny.refreshStrategy === 'function') {
              try {
                const result = stageAny.refreshStrategy();
                if (result && typeof result.catch === 'function') {
                  result.catch((e: any) => {
                    console.error('[IVS Panel] Error refreshing strategy after screen share ended:', e);
                  });
                }
              } catch (e) {
                console.error('[IVS Panel] Error calling refreshStrategy:', e);
              }
            }
          }
          
          screenShareStreamRef.current = null;
          screenShareMediaStreamRef.current = null;
        };
        
        screenShareMediaStreamRef.current = stream;
        
        // Create LocalStageStream from screen share tracks (video and potentially audio)
        const stageAny = screenShareStageRef.current as any;
        let localVideoStageStream: any;
        let localAudioStageStream: any = null;
        
        try {
          const videoTrack = stream.getVideoTracks()[0];
          if (!videoTrack) {
            throw new Error('No video track found in screen share stream');
          }
          
          if (typeof LocalStageStream !== 'undefined') {
            localVideoStageStream = new (LocalStageStream as any)(videoTrack);
          } else {
            throw new Error('LocalStageStream not available');
          }
          
          // Check for system audio track from getDisplayMedia (raw track: WebAudio+MediaStreamDestination was unreliable / silent in some browsers)
          const audioTrack = stream.getAudioTracks()[0];
          if (audioTrack) {
            logger.log('[IVS Panel] 🔊 Screen share has system audio track:', audioTrack.label);
            localAudioStageStream = new (LocalStageStream as any)(audioTrack);
            screenShareSystemAudioStreamRef.current = localAudioStageStream;
          } else {
            logger.log('[IVS Panel] ℹ️ No system audio track in screen share (user may have declined or browser doesn\'t support it)');
          }
        } catch (e) {
          console.error('Failed to create LocalStageStream for screen share:', e);
          stream.getTracks().forEach(track => track.stop());
          setScreenShareLoading(false);
          throw e;
        }
        
        // Publish to screen share stage (stage_arn)
        try {
          logger.log('[IVS Panel] 🟢 Adding screen share to screenShareStreamsToPublishRef...');
          const streamsToAdd = [localVideoStageStream];
          if (localAudioStageStream) {
            streamsToAdd.push(localAudioStageStream);
            logger.log('[IVS Panel] 🔊 Including system audio in screen share publish');
          }
          screenShareStreamsToPublishRef.current = [...screenShareStreamsToPublishRef.current, ...streamsToAdd];
          
          logger.log('[IVS Panel] 🟢 Calling refreshStrategy on screen share stage...');
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ Successfully published screen share to stage_arn (video' + (localAudioStageStream ? ' + audio)' : ')'));
            // Delayed second refresh so capture has real frames and subscribers (e.g. Unity) can pick up non-black stream
            setTimeout(() => {
              if (screenShareStageRef.current && screenShareStreamRef.current && typeof (screenShareStageRef.current as any).refreshStrategy === 'function') {
                (screenShareStageRef.current as any).refreshStrategy().then(() => {
                  logger.log('[IVS Panel] ✅ Delayed refreshStrategy completed (may resolve black screen for viewers)');
                }).catch((e: any) => {
                  console.error('[IVS Panel] Delayed refreshStrategy error:', e);
                });
              }
            }, 1200);
          } else {
            throw new Error('refreshStrategy method not available');
          }

          const stageSnap = screenShareStageRef.current as any;
          const sharerParticipantId = String(
            stageSnap?.localParticipant?.id ??
              stageSnap?.localParticipant?.info?.id ??
              screenShareLocalParticipantIdRef.current ??
              ''
          ).trim();
          if (sharerParticipantId) {
            sendIvsScreenSharePublisherToUnityWithRetries(sharerParticipantId, true, 'IVSRealtimePanel-shareOn');
          } else {
            logger.warn('[IVS Panel] Screen share published but local IVS participant id missing; Unity may not route screen audio');
          }
          if (userId && String(userId).trim()) {
            setIvsScreenShareBrowserPlaybackSuppression(true, userId);
          } else {
            logger.warn('[IVS Panel] No userId; presenter may hear own screen audio via AVPro until userId is set');
          }
        } catch (publishError) {
          console.error('[IVS Panel] ❌ Error publishing screen share:', publishError);
          stream.getTracks().forEach(track => track.stop());
          setScreenShareLoading(false);
          clearIvsScreenShareUnitySync();
          throw publishError;
        }
        
        screenShareStreamRef.current = localVideoStageStream;
        setScreenShareEnabled(true);
        setScreenShareLoading(false);
        onScreenShareChange?.(true);
        logger.log('[IVS Panel] 🟢 SCREEN SHARE TURNED ON (stage_arn)');
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
      setScreenShareLoading(false);
      // User may have cancelled the screen share picker
      if ((error as Error).name === 'NotAllowedError') {
        logger.log('[IVS Panel] Screen share cancelled by user');
      }
    }
  };

  const toggleScreenShareRef = useRef<() => Promise<void>>(async () => {});
  toggleScreenShareRef.current = toggleScreenShare;

  // Toggle screen share audio (microphone) on/off - goes to screen share stage
  const toggleScreenShareAudio = async () => {
    if (!screenShareStageRef.current || !isScreenShareStageConnected || !screenShareEnabled) return;

    try {
      if (screenShareAudioEnabled) {
        logger.log('[IVS Panel] 🔴 TURNING OFF SCREEN SHARE AUDIO...');
        
        const stageAny = screenShareStageRef.current as any;
        
        // Stop the microphone stream
        if (screenShareMicStreamRef.current) {
          screenShareMicStreamRef.current.getTracks().forEach((track) => {
            logger.log('[IVS Panel] 🔴 Stopping mic track:', track.kind, track.id);
            track.stop();
          });
          screenShareMicStreamRef.current = null;
        }
        
        // Remove from screen share publish ref
        if (screenShareAudioStreamRef.current) {
          screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
            s => s !== screenShareAudioStreamRef.current
          );
          
          // Refresh strategy to unpublish
          if (typeof stageAny.refreshStrategy === 'function') {
            await stageAny.refreshStrategy();
            logger.log('[IVS Panel] ✅ Screen share audio unpublished');
          }
          
          screenShareAudioStreamRef.current = null;
        }
        
        setScreenShareAudioEnabled(false);
        setScreenShareAudioMuted(false);
        logger.log('[IVS Panel] ✅ SCREEN SHARE AUDIO TURNED OFF');
      } else {
        logger.log('[IVS Panel] 🟢 TURNING ON SCREEN SHARE AUDIO...');
        
        // Get microphone stream with selected device
        const audioConstraints: MediaTrackConstraints = {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        };
        
        if (selectedAudioDeviceId) {
          audioConstraints.deviceId = { exact: selectedAudioDeviceId };
        }
        
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false
        });
        
        logger.log('[IVS Panel] 🟢 Got microphone stream');
        screenShareMicStreamRef.current = micStream;
        
        // Create LocalStageStream from audio track
        const audioTrack = micStream.getAudioTracks()[0];
        if (!audioTrack) {
          throw new Error('No audio track found');
        }
        
        let localStageStream: any;
        if (typeof LocalStageStream !== 'undefined') {
          localStageStream = new (LocalStageStream as any)(audioTrack);
        } else {
          throw new Error('LocalStageStream not available');
        }
        
        screenShareAudioStreamRef.current = localStageStream;
        
        // Add to screen share publish ref and refresh
        screenShareStreamsToPublishRef.current = [...screenShareStreamsToPublishRef.current, localStageStream];
        
        const stageAny = screenShareStageRef.current as any;
        if (typeof stageAny.refreshStrategy === 'function') {
          await stageAny.refreshStrategy();
          logger.log('[IVS Panel] ✅ Screen share audio published');
        }
        
        setScreenShareAudioEnabled(true);
        setScreenShareAudioMuted(false);
        logger.log('[IVS Panel] 🟢 SCREEN SHARE AUDIO TURNED ON');
      }
    } catch (error) {
      console.error('[IVS Panel] Error toggling screen share audio:', error);
    }
  };

  // Mute/unmute screen share audio
  const toggleScreenShareAudioMute = () => {
    if (!screenShareMicStreamRef.current) return;
    
    const audioTracks = screenShareMicStreamRef.current.getAudioTracks();
    audioTracks.forEach(track => {
      track.enabled = screenShareAudioMuted; // Toggle: if muted, enable; if not muted, disable
    });
    
    setScreenShareAudioMuted(!screenShareAudioMuted);
    logger.log('[IVS Panel]', screenShareAudioMuted ? '🔊 Audio unmuted' : '🔇 Audio muted');
  };

  // Change audio device for screen share - uses screen share stage
  const changeScreenShareAudioDevice = async (deviceId: string) => {
    setSelectedAudioDeviceId(deviceId);
    
    // If audio is currently enabled, restart with new device
    if (screenShareAudioEnabled && screenShareMicStreamRef.current && screenShareStageRef.current) {
      logger.log('[IVS Panel] 🔄 Switching audio device to:', deviceId);
      
      const stageAny = screenShareStageRef.current as any;
      
      // Stop current stream
      screenShareMicStreamRef.current.getTracks().forEach(track => track.stop());
      
      // Get new stream with selected device
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false
      });
      
      screenShareMicStreamRef.current = micStream;
      
      // Remove old stream from screen share publish ref
      if (screenShareAudioStreamRef.current) {
        screenShareStreamsToPublishRef.current = screenShareStreamsToPublishRef.current.filter(
          s => s !== screenShareAudioStreamRef.current
        );
      }
      
      // Create new LocalStageStream
      const audioTrack = micStream.getAudioTracks()[0];
      const localStageStream = new (LocalStageStream as any)(audioTrack);
      screenShareAudioStreamRef.current = localStageStream;
      
      // Add new stream and refresh screen share stage
      screenShareStreamsToPublishRef.current = [...screenShareStreamsToPublishRef.current, localStageStream];
      
      if (typeof stageAny.refreshStrategy === 'function') {
        await stageAny.refreshStrategy();
        logger.log('[IVS Panel] ✅ Audio device switched successfully');
      }
      
      // Apply mute state to new track
      if (screenShareAudioMuted) {
        audioTrack.enabled = false;
      }
    }
  };

  // Toggle background effect while camera is running
  const toggleBackgroundEffect = async (newEffect: BackgroundEffect) => {
    if (!isVideoCallConnected) return;
    
    setBackgroundEffect(newEffect);
    
    // If camera is on, we need to reprocess the stream
    if (localVideoEnabled && rawVideoStreamRef.current) {
      logger.log('[IVS Panel] 🔄 Changing background effect to:', newEffect);
      
      // Stop current processing
      stopBackgroundProcessing();
      
      // Get fresh raw stream or use existing
      let rawStream = rawVideoStreamRef.current;
      
      // If tracks are ended, get new ones
      if (rawStream.getVideoTracks().every(t => t.readyState === 'ended')) {
        rawStream = await navigator.mediaDevices.getUserMedia({
          video: { ...IVS_VIDEO_CALL_CAMERA },
          audio: false,
        });
        rawVideoStreamRef.current = rawStream;
      }
      
      let newStream: MediaStream;
      if (newEffect !== 'none') {
        const processedStream = await startBackgroundProcessing(rawStream, newEffect);
        newStream = processedStream || rawStream;
      } else {
        newStream = rawStream;
      }
      
      // Update local stream display
      setLocalStream(newStream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = newStream;
        localVideoRef.current.play().catch(console.error);
      }
      
      // Update published stream on video call stage
      const stageAny = videoCallStageRef.current as any;
      if (stageAny && typeof stageAny.refreshStrategy === 'function') {
        // Create new LocalStageStream
        const videoTrack = newStream.getVideoTracks()[0];
        if (videoTrack && typeof LocalStageStream !== 'undefined') {
          const localStageStream = new (LocalStageStream as any)(videoTrack);
          streamsToPublishRef.current = [localStageStream];
          localVideoStreamRef.current = localStageStream;
          await stageAny.refreshStrategy();
          logger.log('[IVS Panel] ✅ Published stream updated with new effect on video_call_arn');
        }
      }
    }
  };

  // Note: Subscription is handled via event listeners in the connection effect

  // Filter participants to only show those with active video streams
  // Check both the stored streams map and video element refs
  // In debug mode, show all debug participants regardless of streams
  // Limit to max 12 total video publishers (including local user if they're publishing)
  const MAX_VIDEO_PARTICIPANTS = 12;
  
  // Count actual remote publishers
  // Use activePublishers as source of truth (not video element srcObject)
  const remotePublishersCount = debugMode 
    ? activePublishers.size + debugParticipants.length
    : activePublishers.size;
  
  // Total publishers = remote publishers + local user (if publishing)
  const totalPublishers = remotePublishersCount + (localVideoEnabled ? 1 : 0);
  const isAtVideoLimit = totalPublishers >= MAX_VIDEO_PARTICIPANTS;

  const remotesPublishingFiltered = participants.filter((participant) =>
    activePublishers.has(participant.id)
  );
  const maxRemoteTiles = Math.max(
    0,
    MAX_VIDEO_PARTICIPANTS - (localVideoEnabled ? 1 : 0) - (screenShareEnabled ? 1 : 0)
  );

  // Hard-cap how many remote <video> decoders we mount (was uncapped vs activePublishers).
  const participantsWithStreams = debugMode
    ? [...remotesPublishingFiltered.slice(0, maxRemoteTiles), ...debugParticipants]
    : remotesPublishingFiltered.slice(0, maxRemoteTiles);

  // Calculate optimal grid layout based on number of participants
  // Max 12 participants, 2 per row
  const totalTiles = participantsWithStreams.length + (localVideoEnabled ? 1 : 0) + (screenShareEnabled ? 1 : 0);
  const getGridCols = (count: number) => {
    if (count === 0) return 1;
    if (count === 1) return 1;
    // For 2+ participants, use 2 columns for consistent sizing
    return 2;
  };
  
  const gridCols = getGridCols(totalTiles);

  useEffect(() => {
    if (!onExposeLocalVideoControls) {
      return undefined;
    }
    const api: LocalVideoControlsApi = {
      localVideoEnabled,
      isVideoCallConnected,
      localVideoLoading,
      isVideoCallAvailable,
      isFetchingVideoCallToken,
      isAtVideoLimit,
      localStream,
      isCameraPreviewOpen: showPreviewModal,
      cameraPreviewError: segmenterError,
      openCameraPreview: () => {
        void openPreviewModal();
      },
      closeCameraPreview: () => {
        closePreviewModalRef.current();
      },
      toggleLocalVideo: () => toggleLocalVideoRef.current(),
    };
    onExposeLocalVideoControls(api);
    return () => {
      onExposeLocalVideoControls(null);
    };
  }, [
    onExposeLocalVideoControls,
    localVideoEnabled,
    isVideoCallConnected,
    localVideoLoading,
    isVideoCallAvailable,
    isFetchingVideoCallToken,
    isAtVideoLimit,
    localStream,
    showPreviewModal,
    segmenterError,
    openPreviewModal,
  ]);

  useEffect(() => {
    if (!onExposeScreenShareControls) {
      return undefined;
    }
    const api: ScreenShareControlsApi = {
      hasScreenSharePermission,
      screenShareEnabled,
      screenShareLoading,
      isScreenShareStageConnected,
      screenShareQuality,
      setScreenShareQuality: (q: ScreenShareQuality) => setScreenShareQuality(q),
      toggleScreenShare: () => toggleScreenShareRef.current(),
      isPresentationMode,
      togglePresentationMode: () => {
        onTogglePresentationMode?.();
      },
    };
    onExposeScreenShareControls(api);
    return () => {
      onExposeScreenShareControls(null);
    };
  }, [
    onExposeScreenShareControls,
    hasScreenSharePermission,
    screenShareEnabled,
    screenShareLoading,
    isScreenShareStageConnected,
    screenShareQuality,
    isPresentationMode,
    onTogglePresentationMode,
  ]);

  const disableUnityCapture = () => {
    if (window.unityInstance) {
      try {
        window.unityInstance.SendMessage('BrowserBridge', 'DisableKeyboard', '');
        window.unityInstance.SendMessage('BrowserBridge', 'DisableCursorCapture', '');
        logger.log('[Video Panel] Unity capture disabled');
      } catch (error) {
        console.error('[Video Panel] Error disabling Unity capture:', error);
      }
    }
  };

  return (
    <>
    <div 
      className={`unity-ivs-glass-sidebar absolute top-0 right-0 h-full flex flex-col transition-[width,min-width,max-width,box-shadow] duration-300 ease-in-out z-[1000] overflow-y-auto ${
        isCollapsed
          ? 'w-0 min-w-0 max-w-0 overflow-hidden border-0 p-0 shadow-none bg-transparent pointer-events-none'
          : 'w-[20%] max-w-[400px] min-w-[280px]'
      }`}
      style={{ pointerEvents: 'auto' }}
      data-tour-id={dataTourId}
      onClick={(e) => {
        if (!isCollapsed) {
          e.stopPropagation();
          disableUnityCapture();
        }
      }}
    >
      {/* Loading Overlay */}
      {isLoading && !isCollapsed && (
        <div className="unity-ivs-glass-loading absolute inset-0 flex flex-col items-center justify-center z-[1002]">
          <FaSpinner className="animate-spin text-blue-500 mb-4" size={48} />
          <div className="text-white text-lg font-semibold mb-2">Switching Rooms</div>
          <div className="text-[#999] text-sm">Please wait...</div>
        </div>
      )}

      {!isCollapsed && (
        <div className="flex flex-col h-full p-4 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-white/25">
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-white/10">
            <div className="flex flex-col gap-1">
              <h3 className="text-white text-lg font-semibold m-0">{roomName}</h3>
            </div>
            <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-[#999]">
              <span 
                className={`w-2 h-2 rounded-full ${
                  isConnected 
                    ? 'bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.5)]' 
                    : 'bg-yellow-500'
                }`}
              ></span>
              <span>
                {isConnected 
                  ? 'Connected' 
                  : 'Connecting...'}
              </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCollapseToggle();
                }}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                title="Minimize to dock"
              >
                <FaChevronRight size={16} aria-hidden />
              </button>
              {onDisablePanel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    logger.log('[IVS Panel] Disabling panel - will disconnect and cleanup all streams');
                    onDisablePanel();
                  }}
                  className="p-1.5 text-gray-400 hover:text-white hover:bg-red-600/20 rounded transition-colors"
                  title="Close panel (will disconnect from video)"
                >
                  <FaTimes size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Video Control */}
          <div className="mb-4 pb-4 border-b border-white/10">
            {/* Video calls not available message */}
            {!isVideoCallAvailable && !isFetchingVideoCallToken && (
              <div className="px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-md text-gray-400 text-sm text-center mb-2">
                <FaVideoSlash className="inline-block mr-2" />
                Video calls not enabled for this room
              </div>
            )}
            
            {/* Loading video call token */}
            {isFetchingVideoCallToken && (
              <div className="px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-md text-gray-400 text-sm text-center mb-2 flex items-center justify-center gap-2">
                <FaSpinner className="animate-spin" size={14} />
                <span>Connecting to video call...</span>
              </div>
            )}
            
            <div className="flex flex-col gap-2">
              {/* Camera controls in sidebar — hidden when dock owns camera UX */}
              {isVideoCallAvailable && !dockCameraUx && (
              <div className="flex gap-2">
                {localVideoEnabled ? (
                  <button
                    className="flex-1 p-2.5 rounded-md text-white cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 text-base bg-blue-500 border border-blue-500 hover:bg-blue-600"
                    onClick={toggleLocalVideo}
                    disabled={!isVideoCallConnected}
                    title="Turn off camera"
                  >
                    <FaVideo />
                    <span className="text-sm font-medium">Stop Camera</span>
                  </button>
                ) : (
                  <button
                    className={`flex-1 p-2.5 rounded-md text-white cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 text-base bg-[#2a2a2a] border border-[#444] hover:bg-[#3a3a3a] hover:border-[#555] ${!isVideoCallConnected || isAtVideoLimit || isFetchingVideoCallToken ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={openPreviewModal}
                    disabled={!isVideoCallConnected || isAtVideoLimit || isFetchingVideoCallToken}
                    title={!isVideoCallConnected ? 'Video call not connected' : isAtVideoLimit ? 'Video limit reached' : isFetchingVideoCallToken ? 'Connecting...' : 'Preview and start camera'}
                  >
                    <FaEye />
                    <span className="text-sm font-medium">Preview Camera</span>
                  </button>
                )}
              </div>
              )}
              
              {segmenterError && (
                <div className="px-2 py-1.5 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-xs text-center">
                  {segmenterError}
                </div>
              )}
            </div>
            
            {/* Video limit warning */}
            {!localVideoEnabled && isAtVideoLimit && (
              <div className="px-2 py-1.5 mt-2 bg-orange-900/30 border border-orange-700/50 rounded text-orange-300 text-xs text-center">
                Video limit reached ({totalPublishers}/{MAX_VIDEO_PARTICIPANTS} publishers)
              </div>
            )}
          </div>

          {/* Debug Controls */}
          {debugMode && (
            <div className="mb-4 pb-4 border-b border-white/10">
              <div className="text-yellow-400 text-xs font-semibold mb-2 uppercase">Debug Mode</div>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
            <button
                    onClick={addDebugParticipant}
                    disabled={isAtVideoLimit}
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isAtVideoLimit ? 'Video limit reached' : 'Add a mock participant'}
                  >
                    + Add Participant
                  </button>
                  <button
                    onClick={removeDebugParticipant}
                    disabled={debugParticipants.length === 0}
                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Remove last participant"
                  >
                    - Remove
            </button>
          </div>
                <button
                  onClick={clearDebugParticipants}
                  disabled={debugParticipants.length === 0}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Clear all participants"
                >
                  Clear All ({debugParticipants.length})
                </button>
                {isAtVideoLimit && (
                  <div className="px-2 py-1 bg-yellow-900/30 border border-yellow-700/50 rounded text-yellow-300 text-xs text-center">
                    Max {MAX_VIDEO_PARTICIPANTS} participants reached
                  </div>
                )}
              </div>
            </div>
          )}

          {showParticipantVideoGrid && (
          <div 
            className="grid flex-1 gap-2 overflow-y-auto place-items-start [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-white/25"
            style={{ 
              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
              gridAutoRows: 'min-content',
              alignContent: 'start'
            }}
          >

          {totalTiles === 0 ? (
              <div className="col-span-2 flex items-center justify-center text-[#666] text-sm py-4">
              No active video streams
            </div>
          ) : ((
              <>
              {/* Local video - only show if enabled */}
              {showLocalPreviewInSidebar && localVideoEnabled && (
                <div className="relative aspect-video bg-[#0a0a0a] rounded-md overflow-hidden border-2 border-blue-500 w-full">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover bg-[#0a0a0a]"
                  />
                  {localVideoLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a]/90 text-white z-20">
                      <div className="flex flex-col items-center gap-2">
                        <FaSpinner className="animate-spin" size={32} />
                        <span className="text-xs text-[#999]">Loading video...</span>
                      </div>
                    </div>
                  )}
                  {(!localStream) && !localVideoLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a] text-[#666] z-10">
                      <FaVideoSlash size={24} />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px] font-medium truncate flex items-center gap-1">
                    {localIsIntersectStaff && (
                      <img 
                        src={intersectStaffIcon} 
                        alt="Intersect Staff" 
                        className="w-3 h-3 flex-shrink-0"
                      />
                    )}
                    <span className={localIsIntersectStaff ? 'text-yellow-400' : 'text-white'}>
                      {localUsername}
                    </span>
                  </div>
                </div>
              )}

              {/* Screen share indicator - show when actively sharing (no preview video) */}
              {screenShareEnabled && screenShareMediaStreamRef.current && (
                <div className="relative bg-green-600/20 border-2 border-green-500 rounded-md p-3 w-full">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                      <FaDesktop size={18} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-green-400 font-bold text-sm flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                        Screen Sharing Active
                      </div>
                      <div className="text-gray-400 text-xs mt-0.5">
                        Your screen is being broadcast to this room
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Remote participants with active streams */}
              {participantsWithStreams.map((participant) => {
                // Check if this is a debug participant
                const isDebugParticipant = participant.id.startsWith('debug-');
                
                if (isDebugParticipant) {
                  // Render debug participant with canvas
                  return (
                    <div key={participant.id} className="relative aspect-video bg-[#0a0a0a] rounded-md overflow-hidden border-2 border-yellow-500 w-full">
                      <canvas
                        ref={(el) => {
                          if (el) {
                            debugParticipantVideoRefs.current.set(participant.id, el);
                          }
                        }}
                        className="w-full h-full object-cover bg-[#0a0a0a]"
                      />
                      <div className="absolute top-1 right-1 px-2 py-0.5 bg-yellow-500 text-black text-[9px] font-bold rounded">
                        DEBUG
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px] font-medium truncate flex items-center gap-1">
                        {participant.isIntersectStaff && (
                          <img 
                            src={intersectStaffIcon} 
                            alt="Intersect Staff" 
                            className="w-3 h-3 flex-shrink-0"
                          />
                        )}
                        <span className={participant.isIntersectStaff ? 'text-yellow-400' : 'text-white'}>
                          {participant.userId}
                        </span>
                      </div>
                    </div>
                  );
                }
                
                // Regular participant with real stream
                const streamFromState = participantStreams.get(participant.id);
                const videoElement = participantVideoRefs.current.get(participant.id);
                const streamFromElement = videoElement?.srcObject as MediaStream | null;
                const stream = streamFromState || streamFromElement;

                if (!stream) return null;

                return (
                  <IvsRemoteParticipantVideoTile
                    key={participant.id}
                    participant={participant}
                    stream={stream}
                    participantVideoRefs={participantVideoRefs}
                  />
                );
              })}
              </>
          ))}
          </div>
          )}
        </div>
      )}
    </div>

      {/* Camera preview: fullscreen modal, or portaled into dock host when Unity mounts it */}
      {showPreviewModal &&
        typeof document !== 'undefined' &&
        (() => {
          const host = cameraPreviewDockMountEl;
          const inDock = Boolean(host);
          const card = (
            <div
              className={
                inDock
                  ? 'unity-ivs-glass-modal-card w-full overflow-x-hidden rounded-lg border border-white/10 bg-[#141414] shadow-xl'
                  : 'unity-ivs-glass-modal-card w-[90%] max-w-[600px] overflow-hidden'
              }
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-center justify-between border-b border-white/10 ${
                  inDock ? 'px-3 py-2' : 'px-5 py-4'
                }`}
              >
                <h2 className={`font-semibold text-white ${inDock ? 'text-base' : 'text-lg'}`}>Camera Preview</h2>
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-[#333] hover:text-white"
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className={`relative aspect-video overflow-hidden rounded-lg bg-black ${inDock ? 'm-2' : 'm-4'}`}>
                {previewEffect !== 'none' ? (
                  <canvas ref={previewCanvasRef} className="h-full w-full object-cover" />
                ) : (
                  <video ref={previewVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
                )}

                {previewEffect !== 'none' && (
                  <video ref={previewVideoRef} autoPlay playsInline muted className="hidden" />
                )}

                {isSegmenterLoading && previewEffect !== 'none' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <div className="flex flex-col items-center gap-2 text-white">
                      <FaSpinner className="animate-spin" size={32} />
                      <span className="text-sm">Loading effect...</span>
                    </div>
                  </div>
                )}
              </div>

              <div className={inDock ? 'px-3 pb-3' : 'px-5 pb-4'}>
                <div className="mb-3 text-sm text-gray-400">Choose background:</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-all ${
                      previewEffect === 'none'
                        ? 'border-blue-500 bg-blue-500/20 text-white'
                        : 'border-[#444] bg-[#2a2a2a] text-gray-300 hover:border-[#555] hover:bg-[#333]'
                    }`}
                    onClick={() => setPreviewEffect('none')}
                  >
                    <FaVideo size={20} />
                    <span className="text-sm font-medium">Normal</span>
                  </button>
                  <button
                    type="button"
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-all ${
                      previewEffect === 'blur'
                        ? 'border-purple-500 bg-purple-500/20 text-white'
                        : 'border-[#444] bg-[#2a2a2a] text-gray-300 hover:border-[#555] hover:bg-[#333]'
                    }`}
                    onClick={() => setPreviewEffect('blur')}
                  >
                    <FaUserCircle size={20} />
                    <span className="text-sm font-medium">Blur</span>
                  </button>
                  <button
                    type="button"
                    className={`relative flex flex-col items-center gap-1 overflow-hidden rounded-lg border-2 p-3 transition-all ${
                      previewEffect === 'virtual-neon'
                        ? 'border-cyan-500 ring-2 ring-cyan-500/50'
                        : 'border-[#444] hover:border-[#555]'
                    }`}
                    onClick={() => setPreviewEffect('virtual-neon')}
                  >
                    <img
                      src={virtualBgNeon}
                      alt="Neon"
                      className="absolute inset-0 h-full w-full object-cover opacity-60"
                    />
                    <span className="relative z-10 text-sm font-medium text-white drop-shadow-lg">Neon</span>
                  </button>
                  <button
                    type="button"
                    className={`relative flex flex-col items-center gap-1 overflow-hidden rounded-lg border-2 p-3 transition-all ${
                      previewEffect === 'intersect-background'
                        ? 'border-pink-500 ring-2 ring-pink-500/50'
                        : 'border-[#444] hover:border-[#555]'
                    }`}
                    onClick={() => setPreviewEffect('intersect-background')}
                  >
                    <img
                      src={intersectBg}
                      alt="Intersect Background"
                      className="absolute inset-0 h-full w-full object-cover opacity-60"
                    />
                    <span className="relative z-10 text-sm font-medium text-white drop-shadow-lg">Intersect</span>
                  </button>
                </div>
              </div>

              <div className={`flex gap-3 border-t border-white/10 bg-black/25 ${inDock ? 'px-3 py-3' : 'px-5 py-4'}`}>
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="flex-1 rounded-lg bg-[#333] px-4 py-3 font-medium text-white transition-colors hover:bg-[#444]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={applyPreviewAndStart}
                  disabled={isSegmenterLoading}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
                >
                  <FaCheck />
                  Start Camera
                </button>
              </div>
            </div>
          );

          if (host) {
            return createPortal(card, host);
          }
          return createPortal(
            <div
              className="unity-custom-cursor fixed inset-0 z-[10050] flex items-center justify-center bg-black/80"
              style={{ pointerEvents: 'auto' }}
              onClick={closePreviewModal}
            >
              {card}
            </div>,
            document.body
          );
        })()}

      {typeof document !== 'undefined' &&
        (window as any).__ffIvsFacePinsEnabled === true &&
        (() => {
          const el = document.getElementById('unity-game-face-pins-root');
          return el
            ? createPortal(
                <AvatarFacePins
                  faceStreamsByUserId={facePinStreamsByUserId}
                  localUserId={userId || ''}
                  onLocalFacePinClick={onRequestVideoDock}
                />,
                el
              )
            : null;
        })()}

      {/* Screen Share Tutorial */}
      <ScreenShareTour
        isVisible={showScreenShareTutorial}
        onComplete={() => setShowScreenShareTutorial(false)}
        onSkip={() => setShowScreenShareTutorial(false)}
      />
    </>
  );
};

export default IVSRealtimePanel;

